import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Clock3,
  Bell,
  BarChart3,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Activity,
  ArrowRight,
  TrendingUp,
  AlertTriangle,
  Box,
  Boxes,
  PackageX,
  FileText,
  Printer,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  UserRound,
  Sparkles,
  BriefcaseBusiness,
  MessageCircle,
  Puzzle,
  ListTodo,
} from 'lucide-react';
import axios from '../services/api';
import toast from 'react-hot-toast';
import AddAppointmentModal from '../components/AddAppointmentModal';
import AddPatientRecordModal from '../components/AddPatientRecordModal';
import AppointmentCheckInModal from '../components/AppointmentCheckInModal';
import AvailabilityBoardModal from '../components/AvailabilityBoardModal';
import { FinanceProtectedValue } from '../components/FinanceLockGate';
import useAuthStore from '../store/useAuthStore';
import {
  canBrowseSupplyHubMarketplace,
  canSearchDoctorCircleProfiles,
  hasFinanceRole,
  isOwnerAdminRole,
  normalizeRole,
  ROLE_KEYS,
} from '../utils/rolePermissions';
import { generateAllMonthlyReports, getMonthlyReportCurrentStatus } from '../services/monthlyReportsApi';
import dektalMascot from '../assets/dektal-mascot-final.png';
import {
  dismissDektalNotification,
  generateCurrentDektalNotifications,
  getDektalNotifications,
  getDektalNotificationsUnreadCount,
  markDektalNotificationActionClicked,
  markDektalNotificationRead,
  snoozeDektalNotification,
} from '../services/dektalNotificationApi';

function formatCurrency(amount) {
  return `Rs ${Number(amount || 0).toFixed(2)}`;
}

function formatDisplayDate(dateValue, options = {}) {
  if (!dateValue) return '';

  const date = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(dateValue);

  return new Intl.DateTimeFormat('en-US', {
    month: options.month || 'short',
    day: options.day || 'numeric',
    year: options.year || undefined,
  }).format(date);
}

function formatMonthLabel(monthKey) {
  const [year, month] = String(monthKey || '').split('-').map(Number);
  if (!year || !month) return 'Calendar View';

  const date = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function getCurrentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonthKey(monthKey, delta) {
  const [year, month] = String(monthKey || '').split('-').map(Number);
  const shifted = new Date(year, month - 1 + delta, 1);
  return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}`;
}

function shiftDateKey(dateKey, deltaDays) {
  const [year, month, day] = String(dateKey || '').split('-').map(Number);
  const shifted = new Date(year, month - 1, day + deltaDays);
  return [
    shifted.getFullYear(),
    String(shifted.getMonth() + 1).padStart(2, '0'),
    String(shifted.getDate()).padStart(2, '0'),
  ].join('-');
}

function buildCalendarCells(monthKey) {
  const [year, month] = String(monthKey || '').split('-').map(Number);
  if (!year || !month) return [];

  const firstDayOfMonth = new Date(year, month - 1, 1);
  const firstWeekday = (firstDayOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();
  const previousMonthDays = new Date(year, month - 1, 0).getDate();
  const cells = [];

  for (let index = 0; index < 42; index += 1) {
    const dayOffset = index - firstWeekday + 1;
    let cellDate;
    let isCurrentMonth = true;

    if (dayOffset <= 0) {
      cellDate = new Date(year, month - 2, previousMonthDays + dayOffset);
      isCurrentMonth = false;
    } else if (dayOffset > daysInMonth) {
      cellDate = new Date(year, month - 1, dayOffset);
      isCurrentMonth = false;
    } else {
      cellDate = new Date(year, month - 1, dayOffset);
    }

    cells.push({
      dateKey: [
        cellDate.getFullYear(),
        String(cellDate.getMonth() + 1).padStart(2, '0'),
        String(cellDate.getDate()).padStart(2, '0'),
      ].join('-'),
      dayNumber: cellDate.getDate(),
      isCurrentMonth,
    });
  }

  return cells;
}

function getAppointmentStatusClasses(status) {
  if (status === 'Ongoing' || status === 'Checked-in') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'Completed') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (status === 'Cancelled') return 'bg-rose-50 text-rose-700 border-rose-200';
  return 'bg-amber-50 text-amber-700 border-amber-200';
}

function getVisitTypeClasses(visitType) {
  if (visitType === 'Follow-up') {
    return 'bg-slate-50 text-slate-700 border-slate-200';
  }

  return 'bg-blue-50 text-blue-700 border-blue-200';
}

function getCalendarEventClasses(appointment, isSelected) {
  if (isSelected) {
    return 'border-blue-200 bg-blue-600 text-white shadow-[0_8px_18px_rgba(37,99,235,0.18)]';
  }

  if (appointment.status === 'Cancelled') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }

  if (appointment.status === 'Ongoing') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }

  if (appointment.status === 'Completed') {
    return 'border-blue-200 bg-blue-50 text-blue-700';
  }

  if (appointment.visitType === 'Follow-up') {
    return 'border-blue-100 bg-blue-50 text-blue-700';
  }

  return 'border-slate-200 bg-slate-50 text-slate-700';
}

function getAlertSeverityClasses(severity) {
  if (severity === 'overdue') return 'bg-rose-50 border-rose-200 text-rose-700';
  if (severity === 'due-soon') return 'bg-amber-50 border-amber-200 text-amber-700';
  return 'bg-emerald-50 border-emerald-200 text-emerald-700';
}

function getNotificationAccent(severity) {
  if (severity === 'critical') return 'text-rose-500 bg-rose-50';
  if (severity === 'warning') return 'text-amber-500 bg-amber-50';
  return 'text-blue-500 bg-blue-50';
}

function formatQuantity(value) {
  return Number(value || 0).toFixed(2);
}

function getLowStockStatusBadgeClasses(status) {
  if (status === 'critical') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }

  if (status === 'low') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }

  return 'border-emerald-200 bg-emerald-50 text-emerald-700';
}

function getLowStockStatusLabel(status) {
  if (status === 'critical') return 'Critical';
  if (status === 'low') return 'Low';
  return 'Sufficient';
}

function getMonthlyReportStatusLabel(status) {
  if (status === 'ready') return 'Ready';
  if (status === 'partial') return 'Partial';
  if (status === 'failed') return 'Failed';
  if (status === 'not_generated') return 'Not Generated';
  return 'Pending';
}

function getMonthlyReportStatusClasses(status) {
  if (status === 'ready') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'partial') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (status === 'failed') return 'border-rose-200 bg-rose-50 text-rose-700';
  if (status === 'not_generated') return 'border-slate-200 bg-slate-100 text-slate-700';
  return 'border-blue-200 bg-blue-50 text-blue-700';
}

function getDektalPriorityWeight(priority) {
  const normalized = String(priority || 'low').toLowerCase();
  if (normalized === 'critical') return 4;
  if (normalized === 'high') return 3;
  if (normalized === 'medium') return 2;
  return 1;
}

function getDektalPriorityClasses(priority) {
  const normalized = String(priority || 'low').toLowerCase();
  if (normalized === 'critical') return 'border-rose-200 bg-rose-50 text-rose-700';
  if (normalized === 'high') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (normalized === 'medium') return 'border-blue-200 bg-blue-50 text-blue-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
}

function getDektalSafeMessage(notification) {
  if (notification?.requiresFinanceUnlock) {
    return 'Dektal found finance-related alerts. Unlock Finance Lock to view details.';
  }

  return notification?.message || 'Dektal has a clinic suggestion for you.';
}

function toPositiveNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
}

function GetStartedChecklistBanner({ steps }) {
  const allComplete = steps.every((step) => step.complete);
  if (allComplete) return null;

  const nextStepIndex = steps.findIndex((step) => !step.complete);

  return (
    <section className="rounded-[24px] border border-blue-100 bg-blue-50/70 px-4 py-4 shadow-[0_10px_24px_rgba(37,99,235,0.06)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="shrink-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-700">Get started</p>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">
            Finish your clinic basics
          </h2>
        </div>

        <div className="w-full rounded-3xl border border-slate-200 bg-white/80 px-4 py-4 lg:max-w-md">
          <div className="relative mx-auto max-w-md px-4 pb-1 pt-2">
            <div className="absolute left-9 right-9 top-6 h-0.5 rounded-full bg-slate-200" />
            <div
              className="absolute left-9 top-6 h-0.5 rounded-full bg-emerald-500 transition-all"
              style={{
                width:
                  steps.filter((step) => step.complete).length <= 1
                    ? '0%'
                    : steps.filter((step) => step.complete).length === 2
                    ? '50%'
                    : '100%',
              }}
            />
            <div className="relative grid grid-cols-3 gap-2">
          {steps.map((step, index) => {
            const isNext = index === nextStepIndex;
            return (
              <button
                key={step.title}
                type="button"
                onClick={step.action}
                className="group flex min-w-0 flex-col items-center text-center"
              >
                <span
                  className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition ${
                    step.complete
                      ? 'border-emerald-500 bg-emerald-500 text-white shadow-[0_8px_18px_rgba(16,185,129,0.18)]'
                    : isNext
                      ? 'border-rose-600 bg-rose-600 text-white shadow-[0_8px_18px_rgba(225,29,72,0.14)]'
                      : 'border-rose-200 bg-rose-50 text-rose-700'
                  }`}
                >
                  {step.complete ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
                </span>
                <span
                  className={`mt-2 block max-w-[94px] text-xs font-semibold leading-4 ${
                    step.complete ? 'text-emerald-700' : 'text-rose-700'
                  }`}
                >
                  {step.title}
                </span>
              </button>
            );
          })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function formatDektalSourceModule(value) {
  return String(value || 'clinic')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDektalNotificationTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
}

function sortDektalNotifications(rows) {
  return [...rows].sort((left, right) => {
    const priorityDiff = getDektalPriorityWeight(right.priority) - getDektalPriorityWeight(left.priority);
    if (priorityDiff !== 0) return priorityDiff;

    return new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime();
  });
}

function normalizeDektalActionRoute(route) {
  const value = String(route || '').trim();
  if (!value) return '/dashboard';

  const mapping = {
    '/appointments': '/dashboard',
    '/payments': '/emi-reminders',
    '/business-reports': '/reports',
    '/revenue-report': '/reports',
    '/assistant': '/dashboard',
  };

  return mapping[value] || value;
}

function buildDektalSafeActionParams(params) {
  const parsedParams = typeof params === 'string'
    ? (() => {
        try {
          return JSON.parse(params);
        } catch (error) {
          return null;
        }
      })()
    : params;

  if (!parsedParams || typeof parsedParams !== 'object' || Array.isArray(parsedParams)) {
    return null;
  }

  const unsafeKeys = new Set(['tenantId', 'clinicId']);
  const safeParams = {};
  Object.entries(parsedParams).forEach(([key, value]) => {
    if (unsafeKeys.has(key)) return;
    if (value === undefined || value === null || value === '') return;
    if (typeof value === 'object') return;
    safeParams[key] = String(value);
  });

  return Object.keys(safeParams).length > 0 ? safeParams : null;
}

function getActionKey(appointmentId, actionType) {
  return `${appointmentId}:${actionType}`;
}

function buildSchedulingNote(type, appointment, note) {
  const normalizedNote = String(note || '').trim();
  const base = `Rescheduled from ${appointment.date} ${appointment.time}`;

  return normalizedNote ? `${base}. ${normalizedNote}` : base;
}

const CALENDAR_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SURFACE_CARD_CLASSES =
  'overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]';
const GHOST_ACTION_BUTTON_CLASSES =
  'rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50';
const queueBorderClass = (section, item) => {
  const state = String(item?.state || '').toUpperCase();
  const label = `${section?.key || ''} ${section?.title || ''}`.toLowerCase();
  if (item?.isOverdue || state.includes('OVERDUE') || label.includes('overdue') || label.includes('attention')) return 'border-rose-400';
  if (state === 'READY_FOR_BILLING' || state === 'INVENTORY_REVIEW' || label.includes('ready for billing') || label.includes('inventory review')) return 'border-emerald-400';
  return 'border-blue-300';
};

export default function Dashboard() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const capabilitySet = new Set(user?.capabilities || []);
  const hasCapability = (capability, fallback = true) => Array.isArray(user?.capabilities) ? capabilitySet.has(capability) : fallback;
  const roleKey = normalizeRole(user?.role);
  const canViewDashboardFinance = hasCapability('finance.view', hasFinanceRole(roleKey));
  const canViewMonthlyReportStatus = canViewDashboardFinance;
  const canGenerateMonthlyReportsFromDashboard = isOwnerAdminRole(roleKey);
  const canRestockFromDashboard = hasCapability('inventory.restock', [ROLE_KEYS.OWNER_ADMIN, ROLE_KEYS.DOCTOR].includes(roleKey));
  const canViewTeamAttendanceExceptions = [ROLE_KEYS.OWNER_ADMIN, ROLE_KEYS.DOCTOR].includes(roleKey);
  const canOpenDoctorCircleFromDashboard = canSearchDoctorCircleProfiles(roleKey);
  const canOpenSupplyHubFromDashboard = canBrowseSupplyHubMarketplace(roleKey);
  const [isApptModalOpen, setIsApptModalOpen] = useState(false);
  const [appointmentModalInitialData, setAppointmentModalInitialData] = useState(null);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [patientModalMode, setPatientModalMode] = useState('patientOnly');
  const [remindingAppointmentId, setRemindingAppointmentId] = useState(null);
  const [rowActionLoadingKey, setRowActionLoadingKey] = useState('');
  const [dashboardRefreshing, setDashboardRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    todayAppointments: [],
    pendingFollowUps: [],
    emiAlerts: [],
    notifications: [],
    todayRevenue: {
      billed: 0,
      collected: 0,
      outstanding: 0,
      invoicesCount: 0,
    },
  });
  const [encounterQueues, setEncounterQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lowStockLoading, setLowStockLoading] = useState(true);
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [lowStockAccessRestricted, setLowStockAccessRestricted] = useState(false);
  const [attendanceExceptionsLoading, setAttendanceExceptionsLoading] = useState(true);
  const [attendanceExceptionsAccessRestricted, setAttendanceExceptionsAccessRestricted] = useState(false);
  const [attendanceExceptionsError, setAttendanceExceptionsError] = useState('');
  const [attendanceExceptions, setAttendanceExceptions] = useState({
    date: '',
    counts: {
      absent: 0,
      late: 0,
      missingCheckoutToday: 0,
      missingCheckoutPreviousDays: 0,
      pendingCorrectionIssues: 0,
    },
    absentStaff: [],
    lateStaff: [],
    missingCheckout: {
      today: [],
      previousDays: [],
    },
  });
  const [monthlyReportStatusLoading, setMonthlyReportStatusLoading] = useState(true);
  const [monthlyReportStatusAccessRestricted, setMonthlyReportStatusAccessRestricted] = useState(false);
  const [monthlyReportStatusFinanceLocked, setMonthlyReportStatusFinanceLocked] = useState(false);
  const [monthlyReportStatusError, setMonthlyReportStatusError] = useState('');
  const [monthlyReportStatus, setMonthlyReportStatus] = useState({
    reportMonth: null,
    reportYear: null,
    generatedCount: 0,
    failedCount: 0,
    totalTypes: 0,
    status: 'not_generated',
    reports: [],
  });
  const [monthlyReportGenerateLoading, setMonthlyReportGenerateLoading] = useState(false);
  const [dektalSuggestionsLoading, setDektalSuggestionsLoading] = useState(true);
  const [dektalSuggestionsRefreshing, setDektalSuggestionsRefreshing] = useState(false);
  const [dektalSuggestionsError, setDektalSuggestionsError] = useState('');
  const [dektalSuggestions, setDektalSuggestions] = useState([]);
  const [dektalUnreadCount, setDektalUnreadCount] = useState(0);
  const [dektalSuggestionActionKey, setDektalSuggestionActionKey] = useState('');
  const [onboardingChecklist, setOnboardingChecklist] = useState({
    setupComplete: Boolean(user?.setupCompleted),
    inventoryComplete: false,
    pricingComplete: false,
  });

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(getCurrentMonthKey());
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarAppointments, setCalendarAppointments] = useState([]);
  const [selectedCalendarAppointment, setSelectedCalendarAppointment] = useState(null);

  const [appointmentActionModal, setAppointmentActionModal] = useState({
    open: false,
    type: '',
    appointment: null,
  });
  const [appointmentDetailModal, setAppointmentDetailModal] = useState({
    open: false,
    appointment: null,
  });
  const [checkInModal, setCheckInModal] = useState({
    open: false,
    loading: false,
    submitting: false,
    appointment: null,
    availableChairs: [],
    availableDoctors: [],
  });
  const [selectedChair, setSelectedChair] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilitySaving, setAvailabilitySaving] = useState(false);
  const [availabilityDeletingId, setAvailabilityDeletingId] = useState(null);
  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [availabilityDraft, setAvailabilityDraft] = useState({
    id: null,
    name: '',
    role: 'doctor',
    startTime: '09:00',
    endTime: '17:00',
    notes: '',
    isActive: true,
  });
  const [actionDate, setActionDate] = useState('');
  const [actionTime, setActionTime] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const fetchOnboardingChecklist = async () => {
    try {
      const [setupRes, inventoryRes, pricingRes] = await Promise.all([
        axios.get('/setup').catch(() => ({ data: null })),
        axios.get('/inventory/current').catch(() => ({ data: [] })),
        axios.get('/pricing').catch(() => ({ data: [] })),
      ]);

      const inventoryRows = Array.isArray(inventoryRes.data) ? inventoryRes.data : [];
      const pricingRows = Array.isArray(pricingRes.data) ? pricingRes.data : [];

      setOnboardingChecklist({
        setupComplete: Boolean(setupRes.data?.isSetupCompleted || user?.setupCompleted),
        inventoryComplete: inventoryRows.some(
          (item) => toPositiveNumber(item.currentStockQty) || toPositiveNumber(item.ratePerUnit)
        ),
        pricingComplete: pricingRows.some(
          (treatment) =>
            treatment?.pricingReady !== false &&
            (toPositiveNumber(treatment.profitMargin) ||
              toPositiveNumber(treatment.labCost) ||
              toPositiveNumber(treatment.consultantFees))
        ),
      });
    } catch (error) {
      setOnboardingChecklist((current) => ({
        ...current,
        setupComplete: Boolean(user?.setupCompleted || current.setupComplete),
      }));
    }
  };

  const fetchDashboardData = async (quiet = true) => {
    if (!quiet) {
      setDashboardRefreshing(true);
    }

    if (canViewTeamAttendanceExceptions) {
      setAttendanceExceptionsLoading(true);
      setAttendanceExceptionsError('');
    } else {
      setAttendanceExceptionsLoading(false);
      setAttendanceExceptionsAccessRestricted(true);
      setAttendanceExceptionsError('');
    }

    if (canViewMonthlyReportStatus) {
      setMonthlyReportStatusLoading(true);
      setMonthlyReportStatusAccessRestricted(false);
      setMonthlyReportStatusFinanceLocked(false);
      setMonthlyReportStatusError('');
    } else {
      setMonthlyReportStatusLoading(false);
      setMonthlyReportStatusAccessRestricted(true);
      setMonthlyReportStatusFinanceLocked(false);
      setMonthlyReportStatusError('');
    }

    try {
      const attendanceRequest = canViewTeamAttendanceExceptions
        ? axios.get('/staff-attendance/dashboard-exceptions').catch((error) => {
            console.error('Failed to fetch staff attendance exceptions:', error);
            const restricted = error?.response?.status === 403;
            return {
              data: null,
              _restricted: restricted,
              _failed: true,
              _message:
                error?.response?.data?.error ||
                'Unable to load attendance exceptions right now.',
            };
          })
        : Promise.resolve({ data: null, _restricted: true });

      const monthlyReportStatusRequest = canViewMonthlyReportStatus
        ? getMonthlyReportCurrentStatus().catch((error) => {
            console.error('Failed to fetch monthly report status:', error);
            const statusCode = error?.response?.status;
            const restricted = statusCode === 403;
            const locked = statusCode === 423;
            return {
              data: null,
              _restricted: restricted,
              _locked: locked,
              _failed: !restricted && !locked,
              _message:
                error?.response?.data?.error ||
                'Unable to load monthly report status right now.',
            };
          })
        : Promise.resolve({ data: null, _restricted: true });

      const [summaryRes, lowStockRes, attendanceRes, monthlyReportStatusRes, encounterQueuesRes] = await Promise.all([
        hasCapability('appointment.schedule')
          ? axios.get('/dashboard/summary')
          : Promise.resolve({ data: { todayAppointments: [], pendingFollowUps: [], emiAlerts: [], notifications: [], todayRevenue: { billed: 0, collected: 0, outstanding: 0, invoicesCount: 0 } } }),
        (hasCapability('inventory.view') ? axios.get('/inventory/low-stock-summary') : Promise.resolve({ data: { rows: [] }, _restricted: true })).catch((error) => {
          console.error('Failed to fetch low stock summary:', error);
          const restricted = error?.response?.status === 403;
          return { data: { rows: [] }, _restricted: restricted };
        }),
        attendanceRequest,
        monthlyReportStatusRequest,
        (hasCapability('appointment.schedule') || hasCapability('clinical.view') || hasCapability('billing.invoice.create') || hasCapability('inventory.audit'))
          ? axios.get('/encounters/queues').catch(() => ({ data: { sections: [] } }))
          : Promise.resolve({ data: { sections: [] } }),
      ]);
      setLowStockAccessRestricted(Boolean(lowStockRes?._restricted));
      setAttendanceExceptionsAccessRestricted(Boolean(attendanceRes?._restricted));
      setAttendanceExceptionsError(attendanceRes?._failed ? attendanceRes?._message || 'Unable to load attendance exceptions.' : '');
      setMonthlyReportStatusAccessRestricted(Boolean(monthlyReportStatusRes?._restricted));
      setMonthlyReportStatusFinanceLocked(Boolean(monthlyReportStatusRes?._locked));
      setMonthlyReportStatusError(
        monthlyReportStatusRes?._failed
          ? monthlyReportStatusRes?._message || 'Unable to load monthly report status.'
          : ''
      );

      if (summaryRes.data) {
        setDashboardData({
          todayAppointments: summaryRes.data.todayAppointments || [],
          pendingFollowUps: summaryRes.data.pendingFollowUps || [],
          emiAlerts: summaryRes.data.emiAlerts || [],
          notifications: summaryRes.data.notifications || [],
          todayRevenue: summaryRes.data.todayRevenue || {
            billed: 0,
            collected: 0,
            outstanding: 0,
            invoicesCount: 0,
          },
        });
      }

      setLowStockAlerts(Array.isArray(lowStockRes.data?.rows) ? lowStockRes.data.rows : []);
      setEncounterQueues(Array.isArray(encounterQueuesRes.data?.sections) ? encounterQueuesRes.data.sections : []);

      if (
        !monthlyReportStatusRes?._restricted &&
        !monthlyReportStatusRes?._locked &&
        monthlyReportStatusRes?.data
      ) {
        setMonthlyReportStatus({
          reportMonth: monthlyReportStatusRes.data.reportMonth || null,
          reportYear: monthlyReportStatusRes.data.reportYear || null,
          generatedCount: Number(monthlyReportStatusRes.data.generatedCount || 0),
          failedCount: Number(monthlyReportStatusRes.data.failedCount || 0),
          totalTypes: Number(monthlyReportStatusRes.data.totalTypes || 0),
          status: monthlyReportStatusRes.data.status || 'not_generated',
          reports: Array.isArray(monthlyReportStatusRes.data.reports)
            ? monthlyReportStatusRes.data.reports
            : [],
        });
      } else if (!canViewMonthlyReportStatus) {
        setMonthlyReportStatus({
          reportMonth: null,
          reportYear: null,
          generatedCount: 0,
          failedCount: 0,
          totalTypes: 0,
          status: 'not_generated',
          reports: [],
        });
      }

      if (!attendanceRes?._restricted && attendanceRes?.data) {
        setAttendanceExceptions({
          date: attendanceRes.data.date || '',
          counts: attendanceRes.data.counts || {
            absent: 0,
            late: 0,
            missingCheckoutToday: 0,
            missingCheckoutPreviousDays: 0,
            pendingCorrectionIssues: 0,
          },
          absentStaff: Array.isArray(attendanceRes.data.absentStaff) ? attendanceRes.data.absentStaff : [],
          lateStaff: Array.isArray(attendanceRes.data.lateStaff) ? attendanceRes.data.lateStaff : [],
          missingCheckout: {
            today: Array.isArray(attendanceRes.data?.missingCheckout?.today)
              ? attendanceRes.data.missingCheckout.today
              : [],
            previousDays: Array.isArray(attendanceRes.data?.missingCheckout?.previousDays)
              ? attendanceRes.data.missingCheckout.previousDays
              : [],
          },
        });
      } else if (!canViewTeamAttendanceExceptions) {
        setAttendanceExceptions((current) => ({
          ...current,
          counts: {
            absent: 0,
            late: 0,
            missingCheckoutToday: 0,
            missingCheckoutPreviousDays: 0,
            pendingCorrectionIssues: 0,
          },
          absentStaff: [],
          lateStaff: [],
          missingCheckout: {
            today: [],
            previousDays: [],
          },
        }));
      }
    } catch (err) {
      console.error(err);
      if (!quiet) {
        toast.error('Failed to refresh dashboard');
      }
    } finally {
      setLoading(false);
      setLowStockLoading(false);
      setAttendanceExceptionsLoading(false);
      setMonthlyReportStatusLoading(false);
      setDashboardRefreshing(false);
    }
  };

  const openRestockFromDashboard = (item) => {
    if (canOpenSupplyHubFromDashboard) {
      const params = new URLSearchParams({ tab: 'restock' });
      if (item?.consumableId) {
        params.set('consumableId', String(item.consumableId));
      }
      navigate(`/supply-hub?${params.toString()}`, {
        state: {
          source: 'dashboard_low_stock',
          consumableId: item?.consumableId || null,
          domain: item?.domain || '',
        },
      });
      return;
    }

    if (!item?.consumableId) {
      navigate('/inventory');
      return;
    }

    const params = new URLSearchParams({ restockId: String(item.consumableId) });
    if (item.domain) {
      params.set('restockDomain', String(item.domain));
    }

    navigate(`/inventory?${params.toString()}`, {
      state: {
        openRestockForConsumableId: item.consumableId,
        openRestockForDomain: item.domain || '',
      },
    });
  };

  const openStaffAttendanceFromDashboard = () => {
    navigate('/team-attendance', {
      state: {
        dashboardFocusDate: attendanceExceptions?.date || null,
      },
    });
  };

  const openMonthlyReportsFromDashboard = () => {
    if (monthlyReportStatus?.reportMonth && monthlyReportStatus?.reportYear) {
      navigate('/monthly-reports', {
        state: {
          dashboardMonth: monthlyReportStatus.reportMonth,
          dashboardYear: monthlyReportStatus.reportYear,
        },
      });
      return;
    }

    navigate('/monthly-reports');
  };

  const openAssistantFromDashboard = () => {
    window.dispatchEvent(new CustomEvent('dektal-assistant-open'));
  };

  const generateMonthlyReportsFromDashboard = async () => {
    if (!canGenerateMonthlyReportsFromDashboard) {
      toast.error('Only Owner/Admin can generate monthly reports.');
      return;
    }

    const month = Number(monthlyReportStatus?.reportMonth);
    const year = Number(monthlyReportStatus?.reportYear);
    if (!month || !year) {
      toast.error('Monthly report period is unavailable. Open Monthly Reports and try again.');
      return;
    }

    setMonthlyReportGenerateLoading(true);
    try {
      const response = await generateAllMonthlyReports({
        month,
        year,
      });
      toast.success(`Generated ${Number(response?.count || 0)} monthly reports.`);
      await fetchDashboardData(false);
      await fetchDektalDashboardSuggestions({ quiet: true, generateCurrent: true });
    } catch (error) {
      const statusCode = error?.response?.status;
      if (statusCode === 423) {
        toast.error('Finance Lock is enabled. Unlock finance access first in Monthly Reports.');
      } else if (statusCode === 403) {
        toast.error(error?.response?.data?.error || 'You do not have permission to generate reports.');
      } else {
        toast.error(error?.response?.data?.error || 'Failed to generate monthly reports.');
      }
    } finally {
      setMonthlyReportGenerateLoading(false);
    }
  };

  const fetchDektalDashboardSuggestions = async ({ quiet = true, generateCurrent = false } = {}) => {
    if (!quiet) {
      setDektalSuggestionsRefreshing(true);
    }
    setDektalSuggestionsError('');

    try {
      if (generateCurrent) {
        try {
          await generateCurrentDektalNotifications();
        } catch (generateError) {
          console.error('Failed to generate current Dektal suggestions:', generateError);
          if (!quiet) {
            toast.error(
              generateError?.response?.data?.error ||
              'Dektal could not refresh suggestions, but the dashboard will still load.'
            );
          }
        }
      }

      const [notificationsPayload, unreadPayload] = await Promise.all([
        getDektalNotifications({
          includeRead: false,
          includeDismissed: false,
          includeArchived: false,
          limit: 8,
        }),
        getDektalNotificationsUnreadCount().catch((error) => {
          console.error('Failed to fetch Dektal unread count:', error);
          return { unreadCount: 0 };
        }),
      ]);

      const rows = Array.isArray(notificationsPayload?.rows) ? notificationsPayload.rows : [];
      setDektalSuggestions(sortDektalNotifications(rows).slice(0, 5));
      setDektalUnreadCount(Number(unreadPayload?.unreadCount || 0));
    } catch (error) {
      console.error('Failed to fetch Dektal suggestions:', error);
      const statusCode = error?.response?.status;
      setDektalSuggestions([]);
      setDektalSuggestionsError(
        statusCode === 423
          ? 'Dektal found finance-related alerts. Unlock Finance Lock to view details.'
          : statusCode === 403
          ? 'Dektal suggestions are restricted for your role.'
          : error?.response?.data?.error ||
            'Dektal suggestions are temporarily unavailable.'
      );
    } finally {
      setDektalSuggestionsLoading(false);
      setDektalSuggestionsRefreshing(false);
    }
  };

  const openDektalSuggestionAction = async (notification) => {
    if (!notification?.id) return;

    const route = normalizeDektalActionRoute(notification.actionRoute || '/dashboard');
    const safeParams = buildDektalSafeActionParams(notification.actionParamsJson);
    setDektalSuggestionActionKey(`open-${notification.id}`);

    try {
      await markDektalNotificationActionClicked(notification.id, {
        actionRoute: notification.actionRoute || null,
        sourceModule: notification.sourceModule || null,
      });

      if (safeParams) {
        const urlParams = new URLSearchParams(safeParams);
        navigate(`${route}?${urlParams.toString()}`, { state: safeParams });
      } else {
        navigate(route);
      }

      await fetchDektalDashboardSuggestions({ quiet: true, generateCurrent: false });
    } catch (error) {
      console.error('Failed to open Dektal suggestion action:', error);
      toast.error(error?.response?.data?.error || 'Unable to open this Dektal suggestion.');
    } finally {
      setDektalSuggestionActionKey('');
    }
  };

  const markDektalSuggestionRead = async (notification) => {
    if (!notification?.id) return;
    setDektalSuggestionActionKey(`read-${notification.id}`);

    try {
      await markDektalNotificationRead(notification.id);
      await fetchDektalDashboardSuggestions({ quiet: true, generateCurrent: false });
    } catch (error) {
      console.error('Failed to mark Dektal suggestion read:', error);
      toast.error(error?.response?.data?.error || 'Unable to mark Dektal suggestion as read.');
    } finally {
      setDektalSuggestionActionKey('');
    }
  };

  const dismissDektalSuggestion = async (notification) => {
    if (!notification?.id) return;
    setDektalSuggestionActionKey(`dismiss-${notification.id}`);

    try {
      await dismissDektalNotification(notification.id);
      await fetchDektalDashboardSuggestions({ quiet: true, generateCurrent: false });
    } catch (error) {
      console.error('Failed to dismiss Dektal suggestion:', error);
      toast.error(error?.response?.data?.error || 'Unable to dismiss Dektal suggestion.');
    } finally {
      setDektalSuggestionActionKey('');
    }
  };

  const snoozeDektalSuggestion = async (notification) => {
    if (!notification?.id) return;
    setDektalSuggestionActionKey(`snooze-${notification.id}`);

    try {
      await snoozeDektalNotification(notification.id, { snoozeType: '7_days' });
      await fetchDektalDashboardSuggestions({ quiet: true, generateCurrent: false });
    } catch (error) {
      console.error('Failed to snooze Dektal suggestion:', error);
      toast.error(error?.response?.data?.error || 'Unable to snooze Dektal suggestion.');
    } finally {
      setDektalSuggestionActionKey('');
    }
  };

  const fetchCalendarData = async (monthKey, quiet = true) => {
    if (!quiet) {
      setCalendarLoading(true);
    }

    try {
      const res = await axios.get('/dashboard/calendar', {
        params: { month: monthKey },
      });

      const appointments = Array.isArray(res.data?.appointments) ? res.data.appointments : [];
      setCalendarAppointments(appointments);
      setSelectedCalendarAppointment((previousAppointment) => {
        if (previousAppointment) {
          return appointments.find((item) => item.id === previousAppointment.id) || null;
        }

        return appointments[0] || null;
      });
    } catch (err) {
      console.error(err);
      if (!quiet) {
        toast.error('Failed to load calendar view');
      }
      setCalendarAppointments([]);
      setSelectedCalendarAppointment(null);
    } finally {
      setCalendarLoading(false);
    }
  };

  const refreshDashboardViews = async (options = {}) => {
    const { includeCalendar = isCalendarOpen, quiet = true } = options;

    await fetchDashboardData(quiet);
    await fetchOnboardingChecklist();
    await fetchDektalDashboardSuggestions({ quiet, generateCurrent: !quiet });
    await fetchAvailabilityBoard(true);
    if (includeCalendar) {
      await fetchCalendarData(calendarMonth, quiet);
    }
  };

  useEffect(() => {
    fetchDashboardData(true);
    fetchOnboardingChecklist();
    fetchDektalDashboardSuggestions({ quiet: true, generateCurrent: true });
    fetchAvailabilityBoard(true);
  }, []);

  useEffect(() => {
    if (!isCalendarOpen) return;
    fetchCalendarData(calendarMonth, true);
  }, [calendarMonth, isCalendarOpen]);

  const todayDateString = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  const monthlyReportMissingCount = Math.max(
    0,
    Number(monthlyReportStatus?.totalTypes || 0) - Number(monthlyReportStatus?.generatedCount || 0)
  );
  const monthlyReportLastGeneratedAt = (monthlyReportStatus?.reports || [])
    .map((row) => row?.generatedAt)
    .filter(Boolean)
    .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0];

  const monthlyReportStatusMessage = (() => {
    const status = monthlyReportStatus?.status || 'not_generated';
    if (status === 'ready') return 'Monthly reports are ready.';
    if (status === 'partial') return 'Some monthly reports are pending.';
    if (status === 'failed') return 'Monthly report generation needs attention.';
    if (status === 'not_generated') return 'No monthly reports generated for this period yet.';
    return 'Monthly reports are pending.';
  })();

  const openAppointmentModal = (initialData = null) => {
    setAppointmentModalInitialData(initialData);
    setIsApptModalOpen(true);
  };

  const handleEncounterQueueAction = async (item, action) => {
    const actionKey = `encounter-${item.id}-${action}`;
    setRowActionLoadingKey(actionKey);
    try {
      if (action === 'consultation') {
        await axios.post(`/encounters/${item.id}/${item.clinicalIncomplete || item.state === 'NEEDS_COMPLETION' ? 'resume' : 'start-consultation'}`);
        navigate(`/prescriptions/new?patientId=${item.patientId}&appointmentId=${item.appointmentId}`);
        return;
      }
      if (action === 'billing') {
        await axios.post(`/encounters/${item.id}/send-to-billing`);
        toast.success('Sent to billing');
        await fetchDashboardData(true);
        return;
      }
      if (action === 'invoice') {
        navigate(`/invoices/new?prescriptionId=${item.prescriptionId || ''}&appointmentId=${item.appointmentId}`);
        return;
      }
      if (action === 'inventory') navigate(`/inventory/audit/${item.invoiceId}`);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Unable to update the encounter workflow.');
    } finally {
      setRowActionLoadingKey('');
    }
  };

  const closeAppointmentModal = () => {
    setIsApptModalOpen(false);
    setAppointmentModalInitialData(null);
  };

  const openPatientModal = (mode) => {
    setPatientModalMode(mode);
    setIsPatientModalOpen(true);
  };

  const buildPrescriptionPadRouteFromAppointment = (appointment) => {
    const patientId = appointment?.patientId || appointment?.patient?.id || appointment?.Patient?.id;
    const appointmentId = appointment?.id || appointment?.appointmentId;

    if (!patientId || !appointmentId) {
      return '';
    }

    const params = new URLSearchParams({
      patientId: String(patientId),
      appointmentId: String(appointmentId),
      mode: 'pad',
    });

    if (appointment?.prescriptionId) {
      params.set('id', String(appointment.prescriptionId));
    }

    return `/prescriptions/new?${params.toString()}`;
  };

  const refreshDashboardViewsInBackground = (options = {}) => {
    refreshDashboardViews(options).catch((err) => {
      console.error('Dashboard refresh after appointment save failed:', err);
      toast.error('Appointment saved, but dashboard refresh failed. Please refresh Dashboard later.');
    });
  };

  const handleAppointmentCreated = async (payload = null) => {
    if (payload?.openPad) {
      const appointment = payload?.appointment || {};
      const clinicalPadRoute =
        payload?.clinicalPadRoute || buildPrescriptionPadRouteFromAppointment(appointment);

      if (clinicalPadRoute) {
        toast.success('Appointment saved. Opening Prescription Pad.');
        navigate(clinicalPadRoute);
        refreshDashboardViewsInBackground({ includeCalendar: isCalendarOpen, quiet: true });
        return;
      }

      if (appointment?.patientId) {
        toast('Appointment saved. Prescription Pad route was not available, so patient record was opened.');
        navigate(payload?.patientRecordRoute || `/patients?patientId=${appointment.patientId}`, {
          state: { patientId: appointment.patientId },
        });
        refreshDashboardViewsInBackground({ includeCalendar: isCalendarOpen, quiet: true });
        return;
      }

      toast.error('Appointment saved, but patient record could not be opened.');
      refreshDashboardViewsInBackground({ includeCalendar: isCalendarOpen, quiet: true });
      return;
    }

    await refreshDashboardViews({ includeCalendar: isCalendarOpen, quiet: false });

    if (payload?.openCheckIn && payload?.appointment?.id) {
      await openCheckInModal(payload.appointment);
    }
  };

  const handlePatientCreated = () => {
    refreshDashboardViews({ includeCalendar: isCalendarOpen, quiet: true });
  };

  const resetCheckInModal = () => {
    setCheckInModal({
      open: false,
      loading: false,
      submitting: false,
      appointment: null,
      availableChairs: [],
      availableDoctors: [],
    });
    setSelectedChair('');
    setSelectedDoctor('');
  };

  const fetchAvailabilityBoard = async (quiet = false) => {
    if (!quiet) {
      setAvailabilityLoading(true);
    }

    try {
      const res = await axios.get('/dashboard/availability');
      setAvailabilityRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
    } catch (err) {
      console.error(err);
      if (!quiet) {
        toast.error(err.response?.data?.error || 'Failed to load today\'s availability');
      }
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const openAvailabilityModal = async () => {
    setAvailabilityModalOpen(true);
    await fetchAvailabilityBoard(false);
  };

  const updateAvailabilityDraft = (field, value) => {
    setAvailabilityDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const resetAvailabilityDraft = () => {
    setAvailabilityDraft({
      id: null,
      name: '',
      role: 'doctor',
      startTime: '09:00',
      endTime: '17:00',
      notes: '',
      isActive: true,
    });
  };

  const saveAvailabilityEntry = async () => {
    if (!String(availabilityDraft.name || '').trim()) {
      toast.error('Provider name is required.');
      return;
    }

    setAvailabilitySaving(true);

    try {
      await axios.post('/dashboard/availability', availabilityDraft);
      toast.success('Availability saved');
      resetAvailabilityDraft();
      await fetchAvailabilityBoard(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to save availability');
    } finally {
      setAvailabilitySaving(false);
    }
  };

  const removeAvailabilityEntry = async (availabilityId) => {
    setAvailabilityDeletingId(availabilityId);

    try {
      await axios.delete(`/dashboard/availability/${availabilityId}`);
      toast.success('Availability removed');
      await fetchAvailabilityBoard(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to remove availability');
    } finally {
      setAvailabilityDeletingId(null);
    }
  };

  const openCalendarView = () => {
    setCalendarMonth(getCurrentMonthKey());
    setIsCalendarOpen(true);
  };

  const closeCalendarView = () => {
    setIsCalendarOpen(false);
  };

  const sendFollowUpReminder = async (followUpId) => {
    setRemindingAppointmentId(followUpId);
    try {
      const res = await axios.post(`/follow-ups/${followUpId}/prepare-whatsapp`, { kind: 'scheduled' });
      if (res.data?.whatsappUrl) {
        window.open(res.data.whatsappUrl, '_blank', 'noopener,noreferrer');
      }
      toast.success(res.data?.deliveryMode === 'provider' ? 'Follow-up message sent.' : 'Follow-up message handed off to WhatsApp.');
      refreshDashboardViews({ includeCalendar: false, quiet: true });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to prepare reminder');
    } finally {
      setRemindingAppointmentId(null);
    }
  };

  const openCheckInModal = async (appointment) => {
    setCheckInModal({
      open: true,
      loading: true,
      submitting: false,
      appointment,
      availableChairs: [],
      availableDoctors: [],
    });
    setSelectedChair('');
    setSelectedDoctor('');

    try {
      const res = await axios.get(`/dashboard/appointments/${appointment.id}/check-in-options`);

      if (res.data?.requiresSelection === false && res.data?.appointment?.nextUrl && hasCapability('clinical.prescription.open')) {
        resetCheckInModal();
        navigate(res.data.appointment.nextUrl);
        return;
      }

      setCheckInModal({
        open: true,
        loading: false,
        submitting: false,
        appointment: res.data?.appointment || appointment,
        availableChairs: Array.isArray(res.data?.availableChairs) ? res.data.availableChairs : [],
        availableDoctors: Array.isArray(res.data?.availableDoctors) ? res.data.availableDoctors : [],
      });
      setSelectedChair(String(res.data?.appointment?.chairNumber || ''));
      setSelectedDoctor(res.data?.appointment?.doctorAssigned || '');
    } catch (err) {
      console.error(err);
      resetCheckInModal();
      toast.error(err.response?.data?.error || 'Failed to prepare check-in');
    }
  };

  const handleCheckInAppointment = async (appointment) => {
    if (!hasCapability('appointment.check_in')) return;
    if (appointment?.canResume && appointment?.nextUrl && hasCapability('clinical.prescription.open')) {
      navigate(appointment.nextUrl);
      return;
    }

    await openCheckInModal(appointment);
  };

  const handleAppointmentCardClick = (appointment, event) => {
    const target = event?.target;
    if (target?.closest?.('button, a, input, textarea, select')) {
      return;
    }

    setAppointmentDetailModal({
      open: true,
      appointment,
    });
  };

  const closeAppointmentDetailModal = () => {
    setAppointmentDetailModal({
      open: false,
      appointment: null,
    });
  };

  const openPatientHistoryFromAppointment = (appointment) => {
    if (!hasCapability('clinical.view')) return;
    if (!appointment?.patientId) {
      toast.error('Patient record is not linked to this appointment.');
      return;
    }

    navigate(`/patients?patientId=${appointment.patientId}`, {
      state: { patientId: appointment.patientId },
    });
  };

  const openPrescriptionFromAppointment = (appointment, mode = 'pad') => {
    if (!hasCapability('clinical.prescription.open')) return;
    if (!appointment?.patientId) {
      toast.error('Patient record is not linked to this appointment.');
      return;
    }

    const params = new URLSearchParams({
      patientId: String(appointment.patientId),
      appointmentId: String(appointment.id),
    });
    if (appointment.prescriptionId) {
      params.set('id', String(appointment.prescriptionId));
    }
    if (mode === 'pad') {
      params.set('mode', 'pad');
    }
    if (mode === 'treatmentPlan') {
      params.set('mode', 'treatment-plan');
    }

    navigate(`/prescriptions/new?${params.toString()}`);
  };

  const openInvoiceFromAppointment = (appointment) => {
    if (!hasCapability('billing.invoice.create')) return;
    if (appointment?.prescriptionId) {
      const params = new URLSearchParams({
        prescriptionId: String(appointment.prescriptionId),
        appointmentId: String(appointment.id),
      });
      navigate(`/invoices/new?${params.toString()}`);
      return;
    }

    toast('Open the clinical pad first so the invoice can safely pull treatments from the treatment plan.');
    openPrescriptionFromAppointment(appointment, 'treatmentPlan');
  };

  const submitCheckIn = async () => {
    if (!checkInModal.appointment?.id) {
      return;
    }

    const actionKey = getActionKey(checkInModal.appointment.id, 'check-in');
    setRowActionLoadingKey(actionKey);
    setCheckInModal((current) => ({ ...current, submitting: true }));

    try {
      const res = await axios.put(`/dashboard/appointments/${checkInModal.appointment.id}/check-in`, {
        chairNumber: selectedChair,
        doctorAssigned: selectedDoctor,
      });

      toast.success('Appointment checked in');
      resetCheckInModal();
      await refreshDashboardViews({ includeCalendar: isCalendarOpen, quiet: true });

      if (res.data?.nextUrl && hasCapability('clinical.prescription.open')) {
        navigate(res.data.nextUrl);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to check in appointment');
      setCheckInModal((current) => ({ ...current, submitting: false }));
    } finally {
      setRowActionLoadingKey('');
    }
  };

  const openAppointmentActionModal = (type, appointment) => {
    if (type === 'cancel') {
      setCancelReason('');
      setActionDate('');
      setActionTime('');
      setActionNote('');
    } else {
      const defaultDate = appointment.date;
      setActionDate(defaultDate);
      setActionTime(appointment.time || '');
      setActionNote('');
      setCancelReason('');
    }

    setAppointmentActionModal({
      open: true,
      type,
      appointment,
    });
  };

  const closeAppointmentActionModal = () => {
    setAppointmentActionModal({
      open: false,
      type: '',
      appointment: null,
    });
    setActionDate('');
    setActionTime('');
    setActionNote('');
    setCancelReason('');
  };

  const submitAppointmentAction = async () => {
    const { appointment, type } = appointmentActionModal;
    if (!appointment) return;

    const actionKey = getActionKey(appointment.id, type);
    setRowActionLoadingKey(actionKey);

    try {
      if (type === 'cancel') {
        if (!String(cancelReason || '').trim()) {
          toast.error('Cancellation reason is required.');
          setRowActionLoadingKey('');
          return;
        }

        await axios.put(`/dashboard/appointments/${appointment.id}/cancel`, {
          reason: cancelReason.trim(),
        });
        toast.success('Appointment cancelled');
      } else {
        if (!actionDate || !actionTime) {
          toast.error('Please select a valid date and time.');
          setRowActionLoadingKey('');
          return;
        }

        await axios.put(`/dashboard/appointments/${appointment.id}/reschedule`, {
          date: actionDate,
          time: actionTime,
          notes: buildSchedulingNote(type, appointment, actionNote),
        });

        toast.success('Appointment rescheduled');
      }

      closeAppointmentActionModal();
      await refreshDashboardViews({ includeCalendar: isCalendarOpen, quiet: true });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Failed to update appointment');
    } finally {
      setRowActionLoadingKey('');
    }
  };

  const topActions = [
    { name: 'Invoices', capability: 'billing.invoice.create', description: 'View invoices and permitted payment actions.', icon: BookOpen, iconClasses: 'bg-blue-50 text-blue-600', action: () => navigate('/invoices/new') },
    {
      name: 'Add Appointment',
      capability: 'appointment.schedule',
      description: 'Register or locate a patient and move straight into the appointment flow.',
      icon: Plus,
      iconClasses: 'bg-blue-50 text-blue-600',
      action: () => openAppointmentModal(),
    },
    {
      name: 'Business Reports',
      capability: 'finance.view',
      description: 'Open clinic business reports and profitability insights.',
      icon: BarChart3,
      iconClasses: 'bg-blue-50 text-blue-600',
      action: () => navigate('/reports'),
    },
    {
      name: 'Calendar View',
      capability: 'appointment.schedule',
      description: 'Review this month’s appointments, follow-ups, and appointment notes in one place.',
      icon: CalendarDays,
      iconClasses: 'bg-blue-50 text-blue-600',
      action: openCalendarView,
    },
    {
      name: 'EMI Reminders',
      capability: 'billing.emi.manage',
      description: 'Review due follow-ups, EMI alerts, and reminder actions from the dashboard.',
      icon: Bell,
      iconClasses: 'bg-blue-50 text-blue-600',
      action: () => navigate('/emi-reminders'),
    },
    {
      name: 'Lab & Consultant Ledger',
      capability: 'finance.view',
      description: 'Track dental lab cases, bills, payments and consultant fees.',
      icon: BookOpen,
      iconClasses: 'bg-blue-50 text-blue-600',
      action: () => navigate('/lab-consultant-ledger'),
    },
    {
      name: 'Inventory Setup',
      capability: 'inventory.view',
      description: 'Open inventory setup and current stock.',
      icon: Boxes,
      iconClasses: 'bg-blue-50 text-blue-600',
      action: () => navigate('/inventory'),
    },
    {
      name: 'Print Documents',
      capability: 'clinical.view',
      description: 'Open printable prescriptions and invoices for the active workflow.',
      icon: Printer,
      iconClasses: 'bg-blue-50 text-blue-600',
      action: () => navigate('/print-documents'),
    },
    ...(canOpenDoctorCircleFromDashboard
      ? [
          {
            name: "Doctor's Circle",
            description: 'Create and manage your public consultant profile for participating clinics.',
            icon: BriefcaseBusiness,
            iconClasses: 'bg-blue-50 text-blue-600',
            action: () => navigate('/doctors-circle'),
          },
        ]
      : []),
    ...(canOpenSupplyHubFromDashboard
      ? [
          {
            name: 'Dektal Supply Hub',
            description: 'Browse approved dental products and vendors in the marketplace.',
            icon: Box,
            iconClasses: 'bg-cyan-50 text-cyan-700',
            action: () => navigate('/supply-hub'),
          },
          {
            name: 'Restock Consumables',
            description: 'Open restock suggestions and create a safe Supply Hub order request.',
            icon: PackageX,
            iconClasses: 'bg-amber-50 text-amber-700',
            action: () => navigate('/supply-hub?tab=restock', { state: { source: 'dashboard_restock_button' } }),
          },
        ]
      : []),
    {
      name: 'Add-ons',
      description: 'Explore optional tools, upcoming capabilities, and clinic rewards.',
      icon: Puzzle,
      iconClasses: 'bg-teal-50 text-teal-700',
      action: () => navigate('/addons'),
    },
    {
      name: 'Ask Dektal',
      description: 'Open the clinic assistant panel for role-safe answers and action routing.',
      icon: Sparkles,
      iconClasses: 'bg-cyan-50 text-cyan-700',
      action: openAssistantFromDashboard,
    },
  ].filter((action) => !action.capability || hasCapability(action.capability));

  const calendarAppointmentsByDay = {};
  calendarAppointments.forEach((appointment) => {
    const key = appointment.date;
    if (!calendarAppointmentsByDay[key]) {
      calendarAppointmentsByDay[key] = [];
    }
    calendarAppointmentsByDay[key].push(appointment);
  });

  const calendarCells = buildCalendarCells(calendarMonth);
  const onboardingSteps = [
    {
      title: 'Set up your clinic',
      complete: onboardingChecklist.setupComplete,
      action: () => navigate('/setup'),
    },
    {
      title: 'Add your inventory',
      complete: onboardingChecklist.inventoryComplete,
      action: () => navigate('/inventory'),
    },
    {
      title: 'Set your pricing',
      complete: onboardingChecklist.pricingComplete,
      action: () => navigate('/pricing'),
    },
  ];

  return (
    <div className="min-h-screen bg-white px-3 pb-10 pt-5 font-sans md:px-6 lg:px-8">
      <div className="mx-auto max-w-[1440px] space-y-4">
        <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-600">Dashboard</p>
            <h1 className="text-[28px] font-semibold tracking-tight text-slate-900 md:text-[2.2rem]">
              {user?.workRole === 'doctor' ? 'My Clinical Workspace' : user?.workRole === 'reception' ? 'Front Desk Workspace' : user?.workRole === 'billing_accounts' ? 'Billing Workspace' : user?.workRole === 'inventory_manager' ? 'Inventory Workspace' : 'Clinic Dashboard'}
            </h1>
            <p className="text-sm text-slate-500">Your role-aware view of the clinic work you can access.</p>
          </div>

          <div className="flex flex-col items-stretch gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 shadow-[0_6px_16px_rgba(15,23,42,0.04)]">
              <CalendarDays className="h-4 w-4 text-blue-600" />
              {todayDateString}
            </div>
            <button
              type="button"
              onClick={() => refreshDashboardViews({ includeCalendar: isCalendarOpen, quiet: false })}
              disabled={dashboardRefreshing}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 shadow-[0_6px_16px_rgba(15,23,42,0.04)] transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${dashboardRefreshing ? 'animate-spin' : ''}`} />
              {dashboardRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              type="button"
              onClick={openStaffAttendanceFromDashboard}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 shadow-[0_6px_16px_rgba(15,23,42,0.04)] transition hover:border-blue-200 hover:text-blue-700"
            >
              <Clock3 className="h-4 w-4 text-blue-600" />
              Staff Attendance
            </button>
          </div>
        </header>

        <GetStartedChecklistBanner steps={onboardingSteps} />

        <section className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {topActions.map((action) => (
            <button
              key={action.name}
              type="button"
              onClick={action.action}
              className="group min-h-[132px] rounded-[22px] border border-slate-200/90 bg-white px-3 py-4 text-left shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-all hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-[0_12px_28px_rgba(15,23,42,0.06)] sm:px-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl ${action.iconClasses}`}>
                  <action.icon className="h-5 w-5" />
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-blue-500" />
              </div>
              <div className="mt-3">
                <h2 className="text-sm font-semibold tracking-tight text-slate-900">{action.name}</h2>
              </div>
            </button>
          ))}
        </section>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <div className="space-y-4 xl:col-span-7">
            {encounterQueues.length > 0 ? (
              <section className={SURFACE_CARD_CLASSES}>
                <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-4">
                  <ListTodo className="h-4 w-4 text-blue-600" />
                  <h2 className="text-sm font-semibold tracking-tight text-slate-900">My work queues</h2>
                </div>
                <div className="space-y-4 p-4">
                  {encounterQueues.map((section) => (
                    <div key={section.key}>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{section.title}</p>
                      {section.items?.length ? (
                        <div className="space-y-2">
                          {section.items.slice(0, 6).map((item) => {
                            const canConsult = hasCapability('clinical.edit') && (item.clinicalIncomplete || ['ARRIVED_WAITING', 'CONSULTATION', 'TREATMENT_PLANNED', 'TREATMENT_IN_PROGRESS', 'NEEDS_COMPLETION'].includes(item.state));
                            const canSendToBilling = hasCapability('clinical.edit') && ['CONSULTATION', 'TREATMENT_PLANNED', 'TREATMENT_IN_PROGRESS', 'NEEDS_COMPLETION'].includes(item.state);
                            const canInvoice = hasCapability('billing.invoice.create') && item.state === 'READY_FOR_BILLING';
                            const canCollectPayment = hasCapability('billing.payment.collect') && item.state === 'BILLED_PAYMENT_PENDING';
                            const canInventory = hasCapability('inventory.audit') && item.state === 'INVENTORY_REVIEW' && item.invoiceId;
                            const arrivalAppointment = { id: item.appointmentId, patientId: item.patientId, patientName: item.patientName, phone: item.phone, date: item.appointmentDate, time: item.appointmentTime, doctorAssigned: item.assignedDoctor, prescriptionId: item.prescriptionId, status: item.state === 'SCHEDULED' ? 'Upcoming' : 'Checked-in', visitType: 'Appointment' };
                            const canArrivalCheckIn = section.key === 'reception' && item.state === 'SCHEDULED' && hasCapability('appointment.check_in');
                            return (
                              <div key={item.id} className={`flex flex-col gap-2 rounded-2xl border ${queueBorderClass(section, item)} bg-slate-50 px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between`}>
                                <div>
                                  <p className="text-sm font-semibold text-slate-900">{item.patientName}</p>
                                  <p className="mt-1 text-xs text-slate-500">{item.appointmentTime || 'Scheduled'}{item.assignedDoctor ? ` | ${item.assignedDoctor}` : ''}</p>
                                  {item.scheduleDelayMinutes > 0 ? (
                                    <span className="mt-2 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-800">
                                      Running +{item.scheduleDelayMinutes} min
                                    </span>
                                  ) : null}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {canConsult ? <button type="button" onClick={() => handleEncounterQueueAction(item, 'consultation')} disabled={rowActionLoadingKey === `encounter-${item.id}-consultation`} className={GHOST_ACTION_BUTTON_CLASSES}>Open Pad</button> : null}
                                  {canSendToBilling ? <button type="button" onClick={() => handleEncounterQueueAction(item, 'billing')} disabled={rowActionLoadingKey === `encounter-${item.id}-billing`} className={GHOST_ACTION_BUTTON_CLASSES}>Send to Billing</button> : null}
                                  {canInvoice ? <button type="button" onClick={() => handleEncounterQueueAction(item, 'invoice')} className={GHOST_ACTION_BUTTON_CLASSES}>Open Billing</button> : null}
                                  {canCollectPayment ? <button type="button" onClick={() => handleEncounterQueueAction(item, 'invoice')} className={GHOST_ACTION_BUTTON_CLASSES}>Collect Payment</button> : null}
                                  {canInventory ? <button type="button" onClick={() => handleEncounterQueueAction(item, 'inventory')} className={GHOST_ACTION_BUTTON_CLASSES}>Open Audit</button> : null}
                                  {canArrivalCheckIn ? <button type="button" onClick={() => handleCheckInAppointment(arrivalAppointment)} className={GHOST_ACTION_BUTTON_CLASSES}>Check In</button> : null}
                                  {section.key === 'reception' ? <button type="button" onClick={() => setAppointmentDetailModal({ open: true, appointment: arrivalAppointment })} className={GHOST_ACTION_BUTTON_CLASSES}>View</button> : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : <p className="rounded-xl border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-500">No active items.</p>}
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
            <section className={SURFACE_CARD_CLASSES}>
              <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                    <h2 className="text-sm font-semibold tracking-tight text-slate-900">{user?.workRole === 'doctor' ? "My Today's Appointments" : "Today's Appointments"}</h2>
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-700">
                  {dashboardData.todayAppointments.length} today{user?.workRole === 'doctor' ? ` ? Waiting Patients: ${dashboardData.todayAppointments.filter(appointment => appointment.rawStatus === 'Checked-in').length}` : ''}
                </div>
              </div>

              <div className="p-4">
                {loading ? (
                  <div className="py-12 text-center text-sm text-slate-500">Loading appointments...</div>
                ) : dashboardData.todayAppointments.length > 0 ? (
                  <div className="space-y-2.5">
                    {dashboardData.todayAppointments.map((appointment) => {
                      const canCheckIn = !['Completed', 'Cancelled'].includes(appointment.status);
                      const canReschedule = !['Ongoing', 'Completed', 'Cancelled'].includes(appointment.status);
                      const canCancel = !['Completed', 'Cancelled'].includes(appointment.status);

                      return (
                        <div
                          key={appointment.id}
                          className="cursor-pointer rounded-[20px] border border-slate-200 bg-white px-4 py-3.5 transition hover:border-blue-200 hover:shadow-[0_8px_18px_rgba(15,23,42,0.05)]"
                          onClick={(event) => handleAppointmentCardClick(appointment, event)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setAppointmentDetailModal({
                                open: true,
                                appointment,
                              });
                            }
                          }}
                        >
                          <div className="flex flex-col gap-3">
                            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                              <div className="flex items-start gap-3.5">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-50 text-sm font-semibold text-slate-700">
                                  {appointment.patientName?.charAt(0) || 'P'}
                                </div>

                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-semibold text-slate-900">{appointment.patientName}</p>
                                    <span
                                      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold ${getAppointmentStatusClasses(
                                        appointment.status || 'Upcoming'
                                      )}`}
                                    >
                                      {appointment.status || 'Upcoming'}
                                    </span>
                                    <span
                                      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold ${getVisitTypeClasses(
                                        appointment.visitType
                                      )}`}
                                    >
                                      {appointment.visitType}
                                    </span>
                                    {appointment.bookingSource === 'public_self_booking' && <span className="inline-flex items-center rounded-full bg-teal-700 px-2.5 py-1 text-[10px] font-bold text-white">{appointment.patientSelfRescheduledAt ? 'Patient Rescheduled' : 'Patient Self-Booked'}</span>}
                                    {appointment.scheduleDelayMinutes > 0 ? <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-800">Running +{appointment.scheduleDelayMinutes} min</span> : null}
                                    {appointment.notes ? (
                                      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold text-slate-500">
                                        <FileText className="h-3 w-3" />
                                        Note
                                      </span>
                                    ) : null}
                                  </div>

                                  <p className="mt-1.5 text-xs text-slate-500">
                                    {appointment.phone}
                                    {appointment.patientCode ? ` | ${appointment.patientCode}` : ''}
                                  </p>

                                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                                      {formatDisplayDate(appointment.date)}
                                    </span>
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                                      {appointment.time}
                                    </span>
                                  </div>

                                  {appointment.notes ? (
                                    <p className="mt-2.5 max-w-2xl text-xs leading-5 text-slate-500">
                                      {appointment.notes}
                                    </p>
                                  ) : null}
                                </div>
                              </div>

                              <div className="flex flex-wrap gap-1.5 lg:max-w-[320px] lg:justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleCheckInAppointment(appointment)}
                                  disabled={!canCheckIn || rowActionLoadingKey === getActionKey(appointment.id, 'check-in')}
                                  className="rounded-full bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                                >
                                  {rowActionLoadingKey === getActionKey(appointment.id, 'check-in')
                                    ? appointment.canResume
                                      ? 'Resuming...'
                                      : 'Checking...'
                                    : appointment.canResume
                                    ? 'Resume'
                                    : 'Check-in'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openAppointmentActionModal('reschedule', appointment)}
                                  disabled={!canReschedule}
                                  className={GHOST_ACTION_BUTTON_CLASSES}
                                >
                                  Reschedule
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openAppointmentActionModal('cancel', appointment)}
                                  disabled={!canCancel}
                                  className="rounded-full border border-rose-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openPatientHistoryFromAppointment(appointment)}
                                  className={GHOST_ACTION_BUTTON_CLASSES}
                                >
                                  Open Patient
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openInvoiceFromAppointment(appointment)}
                                  disabled={['Cancelled'].includes(appointment.status)}
                                  className={GHOST_ACTION_BUTTON_CLASSES}
                                >
                                  Invoice
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-[0_6px_16px_rgba(15,23,42,0.04)]">
                      <CalendarDays className="h-6 w-6 text-slate-300" />
                    </div>
                    <p className="mt-3 text-sm font-semibold text-slate-900">No appointments scheduled</p>
                    <p className="mt-1 text-sm text-slate-500">Your schedule is clear for today.</p>
                  </div>
                )}
              </div>
            </section>

            <section className={SURFACE_CARD_CLASSES}>
              <div className="flex flex-col gap-2 border-b border-slate-100 px-5 py-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
                    <Clock3 className="h-4 w-4 text-blue-600" /> Pending Follow-ups
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/follow-ups')}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-blue-600 transition hover:border-blue-200 hover:text-blue-700"
                >
                  Open Reminders <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="p-4">
                {loading ? (
                  <div className="py-10 text-center text-sm text-slate-500">Loading follow-ups...</div>
                ) : dashboardData.pendingFollowUps.length > 0 ? (
                  <div className="space-y-2.5">
                    {dashboardData.pendingFollowUps.map((followUp) => (
                      <div
                        key={followUp.id}
                        className={`rounded-[20px] border px-4 py-3.5 ${
                          followUp.isOverdue ? 'border-rose-200 bg-rose-50/70' : 'border-slate-200 bg-white'
                        }`}
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-slate-900">{followUp.patientName}</p>
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                                  followUp.isOverdue ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {followUp.isOverdue ? 'Overdue' : 'Pending'}
                              </span>
                            </div>
                            <p className="mt-1.5 text-xs text-slate-500">
                              {followUp.phone} | {formatDisplayDate(followUp.date)} | {followUp.time}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {followUp.doctorAssigned || 'Doctor not assigned'} | Confirmation: {followUp.confirmationState || 'pending'}
                            </p>
                            <p className="mt-2 text-xs font-semibold text-slate-600">
                              {followUp.isOverdue
                                ? `${followUp.daysOverdue} day${followUp.daysOverdue === 1 ? '' : 's'} overdue`
                                : followUp.daysFromToday === 0
                                ? 'Due today'
                                : `Due in ${followUp.daysFromToday} day${followUp.daysFromToday === 1 ? '' : 's'}`}
                            </p>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => sendFollowUpReminder(followUp.id)}
                              disabled={remindingAppointmentId === followUp.id || !followUp.manualSendRequired}
                              className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-blue-600 transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-50"
                            >
                              {remindingAppointmentId === followUp.id ? 'Preparing...' : !followUp.manualSendRequired ? 'SENT / HANDED OFF' : (
                                <>
                                  <MessageCircle className="h-4 w-4" />
                                  SEND
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => openAppointmentActionModal('reschedule', { ...followUp, id: followUp.appointmentId })}
                              className={GHOST_ACTION_BUTTON_CLASSES}
                            >
                              Reschedule
                            </button>
                            <button
                              type="button"
                              onClick={() => openPatientHistoryFromAppointment(followUp)}
                              className={GHOST_ACTION_BUTTON_CLASSES}
                            >
                              Open Patient
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                    <CheckCircle2 className="h-9 w-9 text-blue-200" />
                    <p className="mt-3 text-sm font-semibold text-slate-900">All caught up</p>
                    <p className="mt-1 text-sm text-slate-500">No pending or overdue follow-ups.</p>
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="space-y-4 xl:col-span-5">
            <section className={SURFACE_CARD_CLASSES}>
              <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
                <div className="flex min-w-0 items-start gap-3">
                  <img
                    src={dektalMascot}
                    alt="Dektal mascot"
                    className="h-16 w-auto shrink-0 object-contain drop-shadow-[0_12px_18px_rgba(15,23,42,0.18)]"
                  />
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-600">Dektal</p>
                    <h2 className="mt-1 flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
                      <Sparkles className="h-4 w-4 text-cyan-600" /> Dektal Suggestions
                    </h2>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Proactive clinic signals from the Dektal notification hub.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => fetchDektalDashboardSuggestions({ quiet: false, generateCurrent: true })}
                  disabled={dektalSuggestionsRefreshing}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition hover:border-cyan-200 hover:text-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${dektalSuggestionsRefreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              <div className="border-b border-slate-100 px-5 py-2">
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                  <span className="rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 font-semibold text-blue-700">
                    {dektalUnreadCount} unread
                  </span>
                  <span>Also shown through Dektal speech bubbles when urgent.</span>
                </div>
              </div>

              <div className="space-y-2.5 p-4">
                {dektalSuggestionsLoading ? (
                  <div className="py-10 text-center text-sm text-slate-500">Loading Dektal suggestions...</div>
                ) : dektalSuggestionsError ? (
                  <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-8 text-center">
                    <p className="text-sm font-semibold text-slate-900">Dektal suggestions are temporarily unavailable.</p>
                    <p className="mt-1 text-sm text-slate-500">{dektalSuggestionsError}</p>
                  </div>
                ) : dektalSuggestions.length > 0 ? (
                  dektalSuggestions.map((notification) => {
                    const priority = String(notification.priority || 'low').toLowerCase();
                    const actionKey = String(notification.id || '');
                    const isActioning = dektalSuggestionActionKey.includes(`-${actionKey}`);
                    const displayTitle = notification.requiresFinanceUnlock
                      ? 'Finance-related Dektal alert'
                      : notification.title || 'Dektal suggestion';
                    const displayMessage = getDektalSafeMessage(notification);

                    return (
                      <div
                        key={notification.id}
                        className="rounded-[20px] border border-slate-200 bg-slate-50 px-4 py-3.5"
                      >
                        <div className="flex flex-col gap-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getDektalPriorityClasses(
                                    priority
                                  )}`}
                                >
                                  {priority}
                                </span>
                                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                  {formatDektalSourceModule(notification.sourceModule)}
                                </span>
                              </div>
                              <p className="mt-2 text-sm font-semibold text-slate-900">{displayTitle}</p>
                              <p className="mt-1 text-sm leading-6 text-slate-700">{displayMessage}</p>
                              {notification.createdAt ? (
                                <p className="mt-1.5 text-[11px] text-slate-400">
                                  {formatDektalNotificationTime(notification.createdAt)}
                                </p>
                              ) : null}
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            {notification.actionRoute ? (
                              <button
                                type="button"
                                onClick={() => openDektalSuggestionAction(notification)}
                                disabled={isActioning}
                                className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-[11px] font-semibold text-cyan-700 transition hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {dektalSuggestionActionKey === `open-${notification.id}`
                                  ? 'Opening...'
                                  : notification.actionLabel || 'Open'}
                              </button>
                            ) : null}

                            {!notification.isRead ? (
                              <button
                                type="button"
                                onClick={() => markDektalSuggestionRead(notification)}
                                disabled={isActioning}
                                className={GHOST_ACTION_BUTTON_CLASSES}
                              >
                                {dektalSuggestionActionKey === `read-${notification.id}` ? 'Saving...' : 'Mark Read'}
                              </button>
                            ) : null}

                            <button
                              type="button"
                              onClick={() => snoozeDektalSuggestion(notification)}
                              disabled={isActioning}
                              className={GHOST_ACTION_BUTTON_CLASSES}
                            >
                              {dektalSuggestionActionKey === `snooze-${notification.id}` ? 'Saving...' : 'Snooze 7d'}
                            </button>

                            <button
                              type="button"
                              onClick={() => dismissDektalSuggestion(notification)}
                              disabled={isActioning}
                              className="rounded-full border border-rose-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {dektalSuggestionActionKey === `dismiss-${notification.id}` ? 'Saving...' : 'Dismiss'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                    <p className="text-sm font-semibold text-slate-900">Dektal has no urgent suggestions right now.</p>
                    <p className="mt-1 text-sm text-slate-500">The mascot will speak up when a safe clinic signal appears.</p>
                  </div>
                )}
              </div>
            </section>

            {canViewDashboardFinance ? (
            <>
            <section className={SURFACE_CARD_CLASSES}>
              <div className="border-b border-slate-100 px-5 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">Revenue</p>
                    <h2 className="mt-2 text-[28px] font-semibold tracking-tight text-slate-900">
                      <FinanceProtectedValue>{formatCurrency(dashboardData.todayRevenue.billed)}</FinanceProtectedValue>
                    </h2>
                    <p className="mt-1.5 text-xs text-slate-500">
                      <FinanceProtectedValue>
                        {dashboardData.todayRevenue.invoicesCount} invoice
                        {dashboardData.todayRevenue.invoicesCount === 1 ? '' : 's'} billed today
                      </FinanceProtectedValue>
                    </p>
                  </div>

                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                    <TrendingUp className="h-5 w-5" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2.5 p-4">
                <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-3.5 py-3">
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Billed</p>
                  <p className="mt-2 text-base font-semibold text-slate-900">
                    <FinanceProtectedValue>{formatCurrency(dashboardData.todayRevenue.billed)}</FinanceProtectedValue>
                  </p>
                </div>
                <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-3.5 py-3">
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Collected</p>
                  <p className="mt-2 text-base font-semibold text-slate-900">
                    <FinanceProtectedValue>{formatCurrency(dashboardData.todayRevenue.collected)}</FinanceProtectedValue>
                  </p>
                </div>
                <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-3.5 py-3">
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Outstanding</p>
                  <p className="mt-2 text-base font-semibold text-slate-900">
                    <FinanceProtectedValue>{formatCurrency(dashboardData.todayRevenue.outstanding)}</FinanceProtectedValue>
                  </p>
                </div>
              </div>
            </section>
            </>
            ) : null}

            {canViewDashboardFinance ? (
            <section className={SURFACE_CARD_CLASSES}>
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
                    <CreditCard className="h-4 w-4 text-blue-600" /> EMI & Payments
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/emi-reminders')}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-blue-600 transition hover:border-blue-200 hover:text-blue-700"
                >
                  Open Page
                </button>
              </div>

              <div className="p-4">
                {loading ? (
                  <div className="py-10 text-center text-sm text-slate-500">Loading EMI alerts...</div>
                ) : dashboardData.emiAlerts.length > 0 ? (
                  <div className="space-y-2.5">
                    {dashboardData.emiAlerts.slice(0, 6).map((alert) => (
                      <div
                        key={alert.id}
                        className={`rounded-[18px] border px-4 py-3.5 ${getAlertSeverityClasses(alert.severity)}`}
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <p className="text-sm font-semibold">{alert.patientName}</p>
                            <p className="mt-1.5 text-[11px] font-medium opacity-80">
                              {alert.invoiceNumber || 'EMI'} | Due {formatDisplayDate(alert.dueDate)}
                            </p>
                            <p className="mt-2 text-base font-semibold">
                              <FinanceProtectedValue>{formatCurrency(alert.amount)}</FinanceProtectedValue>
                            </p>
                          </div>
                          <div className="flex flex-col items-start gap-3 md:items-end">
                            <p className="text-[11px] font-semibold">
                              {alert.severity === 'overdue'
                                ? `${Math.abs(alert.daysFromToday)} day${Math.abs(alert.daysFromToday) === 1 ? '' : 's'} overdue`
                                : alert.daysFromToday === 0
                                ? 'Due today'
                                : `Due in ${alert.daysFromToday} day${alert.daysFromToday === 1 ? '' : 's'}`}
                            </p>
                            <button
                              type="button"
                              onClick={() => navigate('/emi-reminders')}
                              className="rounded-full border border-current px-3 py-1.5 text-[11px] font-semibold"
                            >
                              Review
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">No collection risks</p>
                    <p className="mt-2 text-sm text-slate-500">All EMI payments are on track.</p>
                  </div>
                )}
              </div>
            </section>
            ) : null}

            <section className={SURFACE_CARD_CLASSES}>
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
                    <PackageX className="h-4 w-4 text-blue-600" /> Low Stock Alerts
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => navigate('/inventory')}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-blue-600 transition hover:border-blue-200 hover:text-blue-700"
                >
                  View Inventory
                </button>
              </div>

              <div className="p-4">
                {lowStockLoading ? (
                  <div className="py-10 text-center text-sm text-slate-500">Loading low-stock alerts...</div>
                ) : lowStockAccessRestricted ? (
                  <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                    <p className="text-sm font-semibold text-slate-900">Inventory alerts are restricted for your role.</p>
                    <p className="mt-1 text-sm text-slate-500">Contact clinic owner/admin for stock alert access.</p>
                  </div>
                ) : lowStockAlerts.length > 0 ? (
                  <div className="space-y-2.5">
                    {lowStockAlerts.slice(0, 6).map((item) => (
                      <div
                        key={`${item.currentStockId || item.consumableId}`}
                        className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5"
                      >
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-slate-900">{item.consumableName}</p>
                              <span
                                className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getLowStockStatusBadgeClasses(item.stockStatus)}`}
                              >
                                {getLowStockStatusLabel(item.stockStatus)}
                              </span>
                            </div>
                            <p className="mt-1.5 text-xs text-slate-500">
                              {item.typeVariant || '-'} | {item.domain || 'Inventory'} | {item.unit || '-'}
                            </p>
                            <p className="mt-2 text-xs font-semibold text-slate-700">
                              Current: {formatQuantity(item.currentStock)} | Restock Qty: {formatQuantity(item.restockQty)} | Min Required: {formatQuantity(item.minimumRequiredQty)} | Stock: {formatQuantity(item.stockPercentage)}%
                            </p>
                          </div>

                          <div className="flex items-center gap-2">
                            {canRestockFromDashboard ? (
                              <button
                                type="button"
                                onClick={() => openRestockFromDashboard(item)}
                                className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold text-blue-700 transition hover:bg-blue-100"
                              >
                                Restock
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => navigate('/inventory')}
                              className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700"
                            >
                              View Inventory
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                    <p className="text-sm font-semibold text-slate-900">All key consumables are sufficiently stocked.</p>
                    <p className="mt-1 text-sm text-slate-500">No low-stock restock alerts are active right now.</p>
                  </div>
                )}
              </div>
            </section>

            {canViewMonthlyReportStatus ? (
              <section className={SURFACE_CARD_CLASSES}>
                <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
                  <div>
                    <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
                      <FileText className="h-4 w-4 text-blue-600" /> Monthly Cloud Reports
                    </h2>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={openMonthlyReportsFromDashboard}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-blue-600 transition hover:border-blue-200 hover:text-blue-700"
                    >
                      Open Monthly Reports
                    </button>
                    {canGenerateMonthlyReportsFromDashboard ? (
                      <button
                        type="button"
                        onClick={generateMonthlyReportsFromDashboard}
                        disabled={monthlyReportGenerateLoading}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${monthlyReportGenerateLoading ? 'animate-spin' : ''}`} />
                        {monthlyReportGenerateLoading ? 'Generating...' : 'Generate Reports'}
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="p-4">
                  {monthlyReportStatusLoading ? (
                    <div className="py-10 text-center text-sm text-slate-500">Loading monthly report status...</div>
                  ) : monthlyReportStatusAccessRestricted ? (
                    <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                      <p className="text-sm font-semibold text-slate-900">Monthly report status is restricted for your role.</p>
                      <p className="mt-1 text-sm text-slate-500">Contact clinic owner/admin for report status access.</p>
                    </div>
                  ) : monthlyReportStatusFinanceLocked ? (
                    <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                      <p className="text-sm font-semibold text-slate-900">Finance Lock is enabled for monthly report status.</p>
                      <p className="mt-1 text-sm text-slate-500">
                        Open Monthly Reports and unlock Finance Lock to view report generation details.
                      </p>
                    </div>
                  ) : monthlyReportStatusError ? (
                    <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                      <p className="text-sm font-semibold text-slate-900">Monthly report status is currently unavailable.</p>
                      <p className="mt-1 text-sm text-slate-500">{monthlyReportStatusError}</p>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${getMonthlyReportStatusClasses(
                              monthlyReportStatus?.status
                            )}`}
                          >
                            {getMonthlyReportStatusLabel(monthlyReportStatus?.status)}
                          </span>
                          <p className="text-sm font-semibold text-slate-900">{monthlyReportStatusMessage}</p>
                        </div>
                        <p className="mt-1.5 text-xs text-slate-500">
                          Period: {monthlyReportStatus?.reportMonth || '-'} / {monthlyReportStatus?.reportYear || '-'}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Generated Types</p>
                          <p className="mt-1.5 text-base font-semibold text-slate-900">
                            {Number(monthlyReportStatus?.generatedCount || 0)}
                          </p>
                        </div>
                        <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Missing Types</p>
                          <p className="mt-1.5 text-base font-semibold text-slate-900">{monthlyReportMissingCount}</p>
                        </div>
                        <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Failed Types</p>
                          <p className="mt-1.5 text-base font-semibold text-slate-900">
                            {Number(monthlyReportStatus?.failedCount || 0)}
                          </p>
                        </div>
                        <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Last Generated</p>
                          <p className="mt-1.5 text-base font-semibold text-slate-900">
                            {monthlyReportLastGeneratedAt
                              ? new Date(monthlyReportLastGeneratedAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            ) : null}

            {canViewTeamAttendanceExceptions ? (
              <section className={SURFACE_CARD_CLASSES}>
                <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
                  <div>
                    <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
                      <Clock3 className="h-4 w-4 text-blue-600" /> Staff Attendance Exceptions
                    </h2>
                  </div>

                  <button
                    type="button"
                    onClick={openStaffAttendanceFromDashboard}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-blue-600 transition hover:border-blue-200 hover:text-blue-700"
                  >
                    Open Staff Attendance
                  </button>
                </div>

                <div className="p-4">
                {attendanceExceptionsLoading ? (
                  <div className="py-10 text-center text-sm text-slate-500">Loading attendance alerts...</div>
                ) : attendanceExceptionsAccessRestricted ? (
                  <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                    <p className="text-sm font-semibold text-slate-900">Attendance alerts are restricted for your role.</p>
                    <p className="mt-1 text-sm text-slate-500">Contact clinic owner/admin for attendance alert access.</p>
                  </div>
                ) : attendanceExceptionsError ? (
                  <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                    <p className="text-sm font-semibold text-slate-900">Attendance alerts are currently unavailable.</p>
                    <p className="mt-1 text-sm text-slate-500">{attendanceExceptionsError}</p>
                  </div>
                ) : (attendanceExceptions?.counts?.absent || 0) > 0 ||
                    (attendanceExceptions?.counts?.late || 0) > 0 ||
                    (attendanceExceptions?.counts?.missingCheckoutToday || 0) > 0 ||
                    (attendanceExceptions?.counts?.missingCheckoutPreviousDays || 0) > 0 ? (
                    <div className="space-y-2.5">
                      <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-700">
                            Absent
                          </span>
                          <p className="text-sm font-semibold text-slate-900">
                            {(attendanceExceptions?.counts?.absent || 0)} staff
                          </p>
                        </div>
                        {(attendanceExceptions?.absentStaff || []).length > 0 ? (
                          <p className="mt-1.5 text-xs text-slate-500">
                            {(attendanceExceptions.absentStaff || [])
                              .slice(0, 4)
                              .map((row) => row?.StaffUser?.name || 'Staff')
                              .join(', ')}
                          </p>
                        ) : null}
                      </div>

                      <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                            Late
                          </span>
                          <p className="text-sm font-semibold text-slate-900">
                            {(attendanceExceptions?.counts?.late || 0)} staff
                          </p>
                        </div>
                        {(attendanceExceptions?.lateStaff || []).length > 0 ? (
                          <p className="mt-1.5 text-xs text-slate-500">
                            {(attendanceExceptions.lateStaff || [])
                              .slice(0, 4)
                              .map((row) => row?.StaffUser?.name || 'Staff')
                              .join(', ')}
                          </p>
                        ) : null}
                      </div>

                      <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-700">
                            Missing Checkout
                          </span>
                          <p className="text-sm font-semibold text-slate-900">
                            {(attendanceExceptions?.counts?.missingCheckoutToday || 0)} today
                            {` | `}
                            {(attendanceExceptions?.counts?.missingCheckoutPreviousDays || 0)} pending from earlier days
                          </p>
                        </div>
                        <p className="mt-1.5 text-xs font-semibold text-slate-700">
                          Pending correction issues: {attendanceExceptions?.counts?.pendingCorrectionIssues || 0}
                        </p>
                        {(attendanceExceptions?.missingCheckout?.today || []).length > 0 ? (
                          <p className="mt-1.5 text-xs text-slate-500">
                            {(attendanceExceptions.missingCheckout.today || [])
                              .slice(0, 4)
                              .map((row) => row?.StaffUser?.name || 'Staff')
                              .join(', ')}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                      <p className="text-sm font-semibold text-slate-900">All staff attendance looks good today.</p>
                      <p className="mt-1 text-sm text-slate-500">No absent, late, or missing checkout exceptions were found.</p>
                    </div>
                  )}
                </div>
              </section>
            ) : null}

            <section className={SURFACE_CARD_CLASSES}>
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4">
                <div>
                  <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
                    <UserRound className="h-4 w-4 text-blue-600" /> Doctor & Staff Availability
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={openAvailabilityModal}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-blue-600 transition hover:border-blue-200 hover:text-blue-700"
                >
                  Manage
                </button>
              </div>

              <div className="p-4">
                {availabilityRows.length > 0 ? (
                  <div className="space-y-2.5">
                    {availabilityRows.slice(0, 4).map((row) => (
                      <div
                        key={row.id}
                        className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5"
                      >
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-slate-900">{row.name}</p>
                              <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                {row.role}
                              </span>
                            </div>
                            <p className="mt-1.5 text-xs text-slate-500">
                              {row.startTime} - {row.endTime}
                            </p>
                          </div>
                          <span
                            className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                              row.isActive
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                : 'border-slate-200 bg-slate-100 text-slate-500'
                            }`}
                          >
                            {row.isActive ? 'Available' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      No availability added
                    </p>
                    <p className="mt-2 text-sm text-slate-500">
                      Add doctor and staff timings before using chair-reserved Check-in.
                    </p>
                  </div>
                )}
              </div>
            </section>

            <section className={SURFACE_CARD_CLASSES}>
              <div className="border-b border-slate-100 px-5 py-4">
                <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-slate-900">
                  <Activity className="h-5 w-5 text-blue-600" /> System Intelligence
                </h2>
              </div>

              <div className="space-y-2.5 p-4">
                {dashboardData.notifications.length > 0 ? (
                  dashboardData.notifications.map((notification, index) => (
                    <div
                      key={`${notification.type || 'notification'}-${index}`}
                      className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5"
                    >
                      <div className="flex gap-3.5">
                        <div
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl ${getNotificationAccent(
                            notification.severity
                          )}`}
                        >
                          <AlertTriangle className="h-4 w-4" />
                        </div>

                        <div className="flex-1">
                          <p className="text-sm leading-6 text-slate-700">{notification.message}</p>
                          {notification.actionUrl ? (
                            <button
                              type="button"
                              onClick={() => navigate(notification.actionUrl)}
                              className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600"
                            >
                              {notification.actionLabel || 'Open'} <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5">
                      <div className="flex gap-3.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-50 text-blue-500">
                          <TrendingUp className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm leading-6 text-slate-700">Chair utilization is steady. Keep follow-up slots filled.</p>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5">
                      <div className="flex gap-3.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                          <PackageX className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm leading-6 text-slate-700">Inventory is stable. No critical stock issues today.</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>

      <AddAppointmentModal
        isOpen={isApptModalOpen}
        closeModal={closeAppointmentModal}
        onAppointmentCreated={handleAppointmentCreated}
        initialData={appointmentModalInitialData}
      />

      <AddPatientRecordModal
        isOpen={isPatientModalOpen}
        onClose={() => setIsPatientModalOpen(false)}
        mode={patientModalMode}
        onPatientCreated={handlePatientCreated}
      />

      {appointmentDetailModal.open && appointmentDetailModal.appointment ? (
        <div className="fixed inset-0 z-[58] flex items-start justify-center overflow-y-auto bg-slate-900/45 p-2 py-4 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="max-h-[calc(100dvh-1rem)] w-full max-w-3xl overflow-y-auto rounded-[24px] border border-slate-200 bg-white shadow-[0_22px_54px_rgba(15,23,42,0.16)] sm:rounded-[26px]">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white px-4 py-4 sm:px-6 sm:py-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">
                  Appointment Detail
                </p>
                <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">
                  {appointmentDetailModal.appointment.patientName || 'Patient'}
                </h2>
                <p className="mt-1.5 text-sm text-slate-500">
                  {appointmentDetailModal.appointment.phone || 'No phone'} |{' '}
                  {formatDisplayDate(appointmentDetailModal.appointment.date, { year: 'numeric' })} |{' '}
                  {appointmentDetailModal.appointment.time}
                </p>
              </div>

              <button
                type="button"
                onClick={closeAppointmentDetailModal}
                className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-4 py-5 sm:px-6">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Status</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {appointmentDetailModal.appointment.status || 'Upcoming'}
                  </p>
                </div>
                <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Visit Type</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {appointmentDetailModal.appointment.visitType || '-'}
                  </p>
                </div>
                <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Doctor</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">
                    {appointmentDetailModal.appointment.doctorAssigned || 'Not assigned'}
                  </p>
                </div>
              </div>

              <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Clinical / Scheduling Notes</p>
                <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-600">
                  {appointmentDetailModal.appointment.notes || 'No appointment notes added yet.'}
                </p>
              </div>

              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                <button
                  type="button"
                  onClick={() => handleCheckInAppointment(appointmentDetailModal.appointment)}
                  disabled={['Completed', 'Cancelled'].includes(appointmentDetailModal.appointment.status)}
                  className="tap-target rounded-full bg-emerald-600 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {appointmentDetailModal.appointment.canResume ? 'Resume Visit' : 'Check In'}
                </button>
                <button
                  type="button"
                  onClick={() => openPatientHistoryFromAppointment(appointmentDetailModal.appointment)}
                  className={GHOST_ACTION_BUTTON_CLASSES}
                >
                  Open Patient History
                </button>
                <button
                  type="button"
                  onClick={() => openPrescriptionFromAppointment(appointmentDetailModal.appointment, 'pad')}
                  disabled={appointmentDetailModal.appointment.status === 'Cancelled'}
                  className={GHOST_ACTION_BUTTON_CLASSES}
                >
                  Open Dental Chart
                </button>
                <button
                  type="button"
                  onClick={() => openPrescriptionFromAppointment(appointmentDetailModal.appointment, 'treatmentPlan')}
                  disabled={appointmentDetailModal.appointment.status === 'Cancelled'}
                  className={GHOST_ACTION_BUTTON_CLASSES}
                >
                  Treatment Plan
                </button>
                <button
                  type="button"
                  onClick={() => openInvoiceFromAppointment(appointmentDetailModal.appointment)}
                  disabled={appointmentDetailModal.appointment.status === 'Cancelled'}
                  className={GHOST_ACTION_BUTTON_CLASSES}
                >
                  Make Invoice
                </button>
                <button
                  type="button"
                  onClick={() => openPrescriptionFromAppointment(appointmentDetailModal.appointment, 'prescription')}
                  disabled={appointmentDetailModal.appointment.status === 'Cancelled'}
                  className={GHOST_ACTION_BUTTON_CLASSES}
                >
                  Make Prescription
                </button>
                <button
                  type="button"
                  onClick={() => openAppointmentActionModal('reschedule', appointmentDetailModal.appointment)}
                  disabled={['Ongoing', 'Completed', 'Cancelled'].includes(appointmentDetailModal.appointment.status)}
                  className={GHOST_ACTION_BUTTON_CLASSES}
                >
                  Reschedule
                </button>
                <button
                  type="button"
                  onClick={() => openAppointmentActionModal('cancel', appointmentDetailModal.appointment)}
                  disabled={['Completed', 'Cancelled'].includes(appointmentDetailModal.appointment.status)}
                  className="rounded-full border border-rose-200 bg-white px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel with Reason
                </button>
                <button
                  type="button"
                  onClick={() => toast('Visit close is handled after billing and inventory audit in the current workflow.')}
                  disabled={appointmentDetailModal.appointment.status === 'Cancelled'}
                  className={GHOST_ACTION_BUTTON_CLASSES}
                >
                  Mark Complete
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <AppointmentCheckInModal
        isOpen={checkInModal.open}
        appointment={checkInModal.appointment}
        loading={checkInModal.loading}
        submitting={checkInModal.submitting}
        availableChairs={checkInModal.availableChairs}
        availableDoctors={checkInModal.availableDoctors}
        selectedChair={selectedChair}
        selectedDoctor={selectedDoctor}
        onSelectChair={setSelectedChair}
        onSelectDoctor={setSelectedDoctor}
        onClose={resetCheckInModal}
        onSubmit={submitCheckIn}
      />

      <AvailabilityBoardModal
        isOpen={availabilityModalOpen}
        dateLabel={todayDateString}
        loading={availabilityLoading}
        saving={availabilitySaving}
        deletingId={availabilityDeletingId}
        rows={availabilityRows}
        draft={availabilityDraft}
        onDraftChange={updateAvailabilityDraft}
        onClose={() => {
          setAvailabilityModalOpen(false);
          resetAvailabilityDraft();
        }}
        onSave={saveAvailabilityEntry}
        onDelete={removeAvailabilityEntry}
        onEdit={(row) =>
          setAvailabilityDraft({
            id: row.id,
            name: row.name,
            role: row.role,
            startTime: row.startTime,
            endTime: row.endTime,
            notes: row.notes || '',
            isActive: Boolean(row.isActive),
          })
        }
      />

      {isCalendarOpen ? (
        <div className="fixed inset-0 z-50 bg-slate-900/35 p-2 backdrop-blur-sm lg:p-6">
          <div className="mx-auto flex h-full max-w-[1520px] flex-col overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-[0_20px_50px_rgba(15,23,42,0.14)] sm:rounded-[26px]">
            <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-4 lg:flex-row lg:items-center lg:justify-between lg:px-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                  <CalendarDays className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">Calendar</p>
                  <h2 className="text-xl font-semibold tracking-tight text-slate-900">Calendar View</h2>
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-2 py-1.5 shadow-[0_6px_16px_rgba(15,23,42,0.04)]">
                  <button
                    type="button"
                    onClick={() => setCalendarMonth((currentMonth) => shiftMonthKey(currentMonth, -1))}
                    className="rounded-full p-2 text-slate-500 transition hover:bg-white hover:text-slate-800"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="min-w-[170px] px-3 text-center text-sm font-semibold text-slate-800">
                    {formatMonthLabel(calendarMonth)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCalendarMonth((currentMonth) => shiftMonthKey(currentMonth, 1))}
                    className="rounded-full p-2 text-slate-500 transition hover:bg-white hover:text-slate-800"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => fetchCalendarData(calendarMonth, false)}
                  disabled={calendarLoading}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-600 shadow-[0_6px_16px_rgba(15,23,42,0.04)] transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${calendarLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>

                <button
                  type="button"
                  onClick={closeCalendarView}
                  className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 shadow-[0_6px_16px_rgba(15,23,42,0.04)] transition hover:text-slate-800"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_360px]">
              <div className="min-h-0 overflow-auto p-3 md:p-5">
                <div className="min-w-[760px] lg:min-w-0">
                  <div className="mb-3 grid grid-cols-7 gap-2">
                    {CALENDAR_WEEKDAYS.map((dayLabel) => (
                      <div
                        key={dayLabel}
                        className="rounded-2xl bg-slate-50 px-3 py-2.5 text-center text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400"
                      >
                        {dayLabel}
                      </div>
                    ))}
                  </div>

                  {calendarLoading ? (
                    <div className="flex h-[70vh] items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
                      Loading calendar...
                    </div>
                  ) : (
                    <div className="grid grid-cols-7 gap-2">
                      {calendarCells.map((cell) => {
                        const dayAppointments = calendarAppointmentsByDay[cell.dateKey] || [];

                        return (
                          <div
                            key={cell.dateKey}
                            className={`min-h-[118px] rounded-[20px] border px-2.5 py-2.5 ${
                              cell.isCurrentMonth
                                ? 'border-slate-200 bg-white'
                                : 'border-slate-100 bg-slate-50/70'
                            }`}
                          >
                            <div className="mb-3 flex items-center justify-between">
                              <span
                                className={`text-sm font-semibold ${
                                  cell.isCurrentMonth ? 'text-slate-900' : 'text-slate-400'
                                }`}
                              >
                                {cell.dayNumber}
                              </span>
                              {dayAppointments.length > 0 ? (
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                                  {dayAppointments.length}
                                </span>
                              ) : null}
                            </div>

                            <div className="space-y-1.5">
                              {dayAppointments.slice(0, 3).map((appointment) => {
                                const isSelected = selectedCalendarAppointment?.id === appointment.id;

                                return (
                                  <button
                                    key={appointment.id}
                                    type="button"
                                    onClick={() => setSelectedCalendarAppointment(appointment)}
                                    className={`w-full rounded-xl border px-2.5 py-2 text-left text-[11px] font-medium transition ${getCalendarEventClasses(
                                      appointment,
                                      isSelected
                                    )}`}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className="truncate">{appointment.patientName}</p>
                                        <p className={`mt-0.5 text-[10px] ${isSelected ? 'text-blue-100' : 'opacity-70'}`}>
                                          {appointment.time}
                                        </p>
                                      </div>
                                      {appointment.notes ? (
                                        <FileText className={`h-3.5 w-3.5 shrink-0 ${isSelected ? 'text-blue-100' : 'opacity-70'}`} />
                                      ) : null}
                                    </div>
                                  </button>
                                );
                              })}

                              {dayAppointments.length > 3 ? (
                                <div className="px-1 text-[10px] font-medium text-slate-400">
                                  +{dayAppointments.length - 3} more
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <aside className="min-h-0 overflow-auto border-l border-slate-100 bg-white p-5 md:p-5">
                <div className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_8px_18px_rgba(15,23,42,0.04)]">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">Appointment Details</p>

                  {selectedCalendarAppointment ? (
                    <div className="mt-4 space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold tracking-tight text-slate-900">
                          {selectedCalendarAppointment.patientName}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                          {selectedCalendarAppointment.phone}
                          {selectedCalendarAppointment.patientCode
                            ? ` | ${selectedCalendarAppointment.patientCode}`
                            : ''}
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold ${getAppointmentStatusClasses(
                            selectedCalendarAppointment.status
                          )}`}
                        >
                          {selectedCalendarAppointment.status}
                        </span>
                        <span
                          className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-semibold ${getVisitTypeClasses(
                            selectedCalendarAppointment.visitType
                          )}`}
                        >
                          {selectedCalendarAppointment.visitType}
                        </span>
                        {selectedCalendarAppointment.bookingSource === 'public_self_booking' && <span className="inline-flex items-center rounded-full bg-teal-700 px-2.5 py-1 text-[10px] font-bold text-white">{selectedCalendarAppointment.patientSelfRescheduledAt ? 'Patient Rescheduled' : 'Patient Self-Booked'}</span>}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-3.5 py-3">
                          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Date</p>
                          <p className="mt-2 text-sm font-semibold text-slate-900">
                            {formatDisplayDate(selectedCalendarAppointment.date, { year: 'numeric' })}
                          </p>
                        </div>
                        <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-3.5 py-3">
                          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Time</p>
                          <p className="mt-2 text-sm font-semibold text-slate-900">
                            {selectedCalendarAppointment.time}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-3.5 py-3">
                        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">Appointment Note</p>
                        <p className="mt-2.5 text-sm leading-6 text-slate-600">
                          {selectedCalendarAppointment.notes || 'No note added yet for this appointment.'}
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleCheckInAppointment(selectedCalendarAppointment)}
                          disabled={
                            ['Completed', 'Cancelled'].includes(selectedCalendarAppointment.status) ||
                            rowActionLoadingKey === getActionKey(selectedCalendarAppointment.id, 'check-in')
                          }
                          className="rounded-full bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                        >
                          {rowActionLoadingKey === getActionKey(selectedCalendarAppointment.id, 'check-in')
                            ? selectedCalendarAppointment.canResume
                              ? 'Resuming...'
                              : 'Checking...'
                            : selectedCalendarAppointment.canResume
                            ? 'Resume'
                            : 'Check-in'}
                        </button>
                        <button
                          type="button"
                          onClick={() => openAppointmentActionModal('cancel', selectedCalendarAppointment)}
                          disabled={['Completed', 'Cancelled'].includes(selectedCalendarAppointment.status)}
                          className="rounded-full border border-rose-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => openAppointmentActionModal('reschedule', selectedCalendarAppointment)}
                          disabled={['Ongoing', 'Completed', 'Cancelled'].includes(selectedCalendarAppointment.status)}
                          className={GHOST_ACTION_BUTTON_CLASSES}
                        >
                          Reschedule
                        </button>
                        <button
                          type="button"
                          onClick={() => openPatientHistoryFromAppointment(selectedCalendarAppointment)}
                          className={GHOST_ACTION_BUTTON_CLASSES}
                        >
                          Patient
                        </button>
                        <button
                          type="button"
                          onClick={() => openPrescriptionFromAppointment(selectedCalendarAppointment, 'pad')}
                          disabled={selectedCalendarAppointment.status === 'Cancelled'}
                          className={GHOST_ACTION_BUTTON_CLASSES}
                        >
                          Dental Chart
                        </button>
                        <button
                          type="button"
                          onClick={() => openInvoiceFromAppointment(selectedCalendarAppointment)}
                          disabled={selectedCalendarAppointment.status === 'Cancelled'}
                          className={GHOST_ACTION_BUTTON_CLASSES}
                        >
                          Invoice
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-[18px] border border-dashed border-slate-200 bg-slate-50 px-5 py-10 text-center">
                      <p className="text-sm font-semibold text-slate-900">Select an appointment</p>
                      <p className="mt-2 text-sm text-slate-500">Click any event to view details.</p>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </div>
      ) : null}

      {appointmentActionModal.open && appointmentActionModal.appointment ? (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-slate-900/45 p-2 py-4 backdrop-blur-sm sm:items-center sm:p-4">
          <div className="max-h-[calc(100dvh-1rem)] w-full max-w-lg overflow-y-auto rounded-[24px] border border-slate-200 bg-white shadow-[0_18px_44px_rgba(15,23,42,0.14)]">
            <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white px-5 py-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">
                  {appointmentActionModal.type === 'cancel'
                    ? 'Cancel Appointment'
                    : 'Reschedule Appointment'}
                </p>
                <h2 className="mt-2 text-lg font-semibold tracking-tight text-slate-900">
                  {appointmentActionModal.appointment.patientName}
                </h2>
                <p className="mt-1.5 text-sm text-slate-500">
                  {formatDisplayDate(appointmentActionModal.appointment.date, { year: 'numeric' })} |{' '}
                  {appointmentActionModal.appointment.time}
                </p>
              </div>

              <button
                type="button"
                onClick={closeAppointmentActionModal}
                className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              {appointmentActionModal.type === 'cancel' ? (
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Cancellation Reason
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Enter the reason for cancelling this appointment"
                    className="mt-2.5 min-h-[120px] w-full rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100 resize-none"
                  />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Appointment Date
                      </label>
                      <input
                        type="date"
                        value={actionDate}
                        onChange={(e) => setActionDate(e.target.value)}
                        className="mt-2.5 w-full rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Appointment Time
                      </label>
                      <input
                        type="time"
                        value={actionTime}
                        onChange={(e) => setActionTime(e.target.value)}
                        className="mt-2.5 w-full rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Scheduling Note
                    </label>
                    <textarea
                      value={actionNote}
                      onChange={(e) => setActionNote(e.target.value)}
                      placeholder="Optional note for the new schedule"
                      className="mt-2.5 min-h-[120px] w-full rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-700 outline-none transition focus:border-blue-200 focus:bg-white focus:ring-2 focus:ring-blue-100 resize-none"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="mobile-sticky-actions flex flex-col items-stretch justify-end gap-3 border-t border-slate-100 bg-white px-5 py-4 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={closeAppointmentActionModal}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={submitAppointmentAction}
                disabled={rowActionLoadingKey === getActionKey(appointmentActionModal.appointment.id, appointmentActionModal.type)}
                className={`rounded-full px-4 py-2 text-sm font-semibold text-white transition ${
                  appointmentActionModal.type === 'cancel'
                    ? 'bg-rose-600 hover:bg-rose-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                } disabled:cursor-not-allowed disabled:bg-slate-300`}
              >
                {rowActionLoadingKey === getActionKey(appointmentActionModal.appointment.id, appointmentActionModal.type)
                  ? 'Saving...'
                  : appointmentActionModal.type === 'cancel'
                  ? 'Cancel Appointment'
                  : 'Save Schedule'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
